// src/components/block/liveStreamManagement/ProductionControlRoom_RemoteDevicesPanel.tsx
//
// v13 — Édition et suppression des sources HLS/Xtream
// v14 — Fix : xtreamAccountIdRef pour stocker l'account.id retourné par addXtreamAccount
//        (évite la lecture du state React périmé dans handleAddXtream)
//        + logs temporaires dans handleRegister pour diagnostiquer le bug device distant
// v15 — Bouton Ajouter grisé si channelActive === false

import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  RefreshCw, Eye, EyeOff, KeyRound,
  Plus, Trash2, Mail, Pencil, X, Check, Video, Unplug, Copy, Radio,
  Smartphone, Tv, Globe, Satellite,
} from 'lucide-react';

import RecipientChipInput from '../../widget/inputs/RecipientChipInput';
import Trigger          from '../../widget/actuators/Trigger';
import AddressBookModal from '../broadcastingChannelManagment/AddressBookModal';
import { useTypedSelector } from '../../../store/useTypeSelector';
import { RootState }        from '../../../store/generalReducer';

import { RemoteDevice, RemoteConnection, DeviceAuthMode } from '../../../dataTypes/liveStreamManagment/types_webrtc';
import { buildDeviceAccessUuid, generateDeviceAccessToken } from '../../../dataTypes/liveStreamManagment/utils_deviceAccess';
import { XtreamCredentials } from '../../../dataTypes/liveStreamManagment/sourceRegistry/types';
import { useProductionControlRoom } from '../../../contexts/ProductionControlRoomContext';
import { useSourceRegistry } from '../../../contexts/liveStreamManagment/SourceRegistryContext';
import {
  updateRemoteDevice,
  isDeviceAccessTokenFree,
  sendDeviceInvitationEmail,
  sendDeviceInvitationWhatsApp,
  sendRtmpCameraInvitation,
  RegisteredRemoteDevice,
} from '../../../backendServices/liveStreamManagment/api_remoteDevices';
import { Recipient        ,
         recipientKey     ,
         recipientDisplay }  from '../../../dataTypes/adminManagment/types_addressBook';
import { isValidRecipient }  from '../../../utils/utils_recipients';
import {
  QUALITY_LABELS, describeSample, NetworkQualityLevel,
} from '../../../backendServices/liveStreamManagment/api_networkQuality';
import { createAdminActionLog } from '../../../backendServices/adminManagment/api';
import {
  getLiveStreams,
  resolveLiveStreamUrl,
} from '../../../backendServices/liveStreamManagment/api_xtreamCode';
import {
  listXtreamAccounts,
  addXtreamAccount,
  getXtreamAccount,
  getXtreamChannels,
  refreshXtreamChannels,
  setSelectedXtreamChannel,
  listAllHlsStreams,
  addHlsStream,
  deleteHlsStream,
  updateHlsStream,
  listRtmpInputs,
  addRtmpInput,
  renameRtmpInput,
  regenerateRtmpInputCredentials,
  deleteRtmpInput,
  getRtmpInputStatus,
  listPtzCameras,
  addPtzCamera,
  deletePtzCamera,
} from '../../../backendServices/liveStreamManagment/api_microservice_sources';
import {
  listXtreamDirectory,
  saveXtreamDirectoryEntry,
  deleteXtreamDirectoryEntry,
  XtreamDirectoryEntry,
} from '../../../backendServices/liveStreamManagment/api_xtreamDirectory';
import {
  listHlsDirectory,
  saveHlsDirectoryEntry,
  deleteHlsDirectoryEntry,
  HlsDirectoryEntry,
} from '../../../backendServices/liveStreamManagment/api_hlsDirectory';
import {
  MicroserviceXtreamAccount,
  MicroserviceHlsStream,
  MicroserviceRtmpStatus,
  MicroservicePtzCamera,
  PtzProtocol,
} from '../../../dataTypes/liveStreamManagment/types_microservice_sources';
import { XtreamLiveStream } from '../../../dataTypes/liveStreamManagment/types_xtreamCode';
import XtreamStreamSelector from './XtreamStreamSelector';
import { SourceAliasMap } from './SourceAliasConfigModal';

import {
  getDeviceSfuInfo,
  isSfuUnavailable,
  isDeviceOnSfu,
} from '../../../backendServices/liveStreamManagment/api_whep';
import { VideoSource, useMediaStream } from '../../../contexts/MediaStreamContext';
import { HlsProbeStatus } from './useHlsProber';

// ── Types locaux ──────────────────────────────────────────────────────────────

type TabId = 'remote' | 'xtream' | 'hls' | 'rtmp' | 'ptz';

// Désactivation temporaire de l'onglet "Caméra PTZ" : reste visible dans la
// barre d'onglets, mais non cliquable. Repasser à `false` pour la réactiver.
const PTZ_TAB_TEMPORAIREMENT_DESACTIVE = true;

interface EditState {
  device_id : string;
  newId     : string;
  password  : string;
  label     : string;
  authMode  : DeviceAuthMode;
}

interface MailState {
  device    : RemoteDevice;
  password  : string;
  // Une seule liste, mails et WhatsApp mélangés : chaque destinataire sait par où
  // il part. Le carnet vient l'alimenter, la saisie libre aussi.
  recipients: Recipient[];
  // Destinataires réellement servis, tels qu'ils s'affichent (le nom quand il
  // vient du carnet) — un envoi peut réussir pour les uns et échouer pour les autres.
  sentTo    : string[];
  sending   : boolean;
  error     : string | null;
  success   : boolean;
}

/**
 * Renvoi des informations d'une caméra RTMP déjà enregistrée — même modale
 * que MailState pour un device Smartphone/Web, mais les informations
 * envoyées sont celles d'une publication (serveur, clé, URL complète) et non
 * un lien de connexion.
 */
interface RtmpMailState {
  camera    : MicroserviceHlsStream;
  recipients: Recipient[];
  sentTo    : string[];
  sending   : boolean;
  error     : string | null;
  success   : boolean;
}

interface EditXtreamState {
  stream           : MicroserviceHlsStream;
  mode             : 'existing' | 'new';
  selectedAccountId: string;
  xtreamServer     : string;
  xtreamUser       : string;
  xtreamPass       : string;
  streams          : XtreamLiveStream[] | null;
  showSelector     : boolean;
  selectedStream   : XtreamLiveStream | null;
  alias            : string;
  loading          : boolean;
  error            : string | null;
}

/**
 * Première étape de l'édition d'une source Xtream : l'alias, et un rappel de ce
 * que la source désigne (compte, serveur, chaîne). Le choix du flux lui-même vit
 * dans EditXtreamState, une fenêtre plus loin — même découpage que pour un flux
 * HLS libre, où l'on nomme d'abord et où l'on change l'adresse ensuite.
 */
interface EditXtreamAliasState {
  stream : MicroserviceHlsStream;
  alias  : string;
  saving : boolean;
  error  : string | null;
}

interface EditHlsState {
  stream : MicroserviceHlsStream;
  url    : string;
  label  : string;
  error  : string | null;
  saving : boolean;
}

/**
 * Bascule P2P / SFU du transport des devices, rendue à l'opérateur le 13/08/2026,
 * masquée le 16/08/2026, RENDUE de nouveau le 27/08/2026 — le SFU est devenu le
 * défaut ce jour-là, et un défaut qu'on ne peut pas contredire est un piège : il
 * faut pouvoir repasser en direct pour comparer, ou pour dépanner un appareil que
 * le SFU laisse muet.
 *
 * Le réglage (settings.deviceTransport) reste ACTIF quoi qu'il arrive : seul le
 * sélecteur disparaît, et le transport continue de suivre la valeur en vigueur —
 * 'sfu' par défaut (DEFAULT_SETTINGS, MediaStreamContext). Masquer ne remet donc
 * rien à zéro.
 */
const SHOW_DEVICE_TRANSPORT_TOGGLE = true;

/** Petit bouton « copier » : copie `text` dans le presse-papiers, confirme 1,5 s. */
const CopyChip: React.FC<{ text: string; label: string }> = ({ text, label }) => {
  const [ done, setDone ] = useState(false);
  return (
    <button type="button" title={label} aria-label={label}
      onClick={() => { navigator.clipboard?.writeText(text).then(() => { setDone(true); setTimeout(() => setDone(false), 1500); }).catch(() => {}); }}
      className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${
        done ? 'bg-emerald-600/30 text-emerald-300 border-emerald-500/40' : 'bg-blue-600/20 text-blue-200 border-blue-500/40 hover:bg-blue-600/40'}`}>
      {done ? <><Check className="w-3.5 h-3.5" /> Copié</> : <><Copy className="w-3.5 h-3.5" /> Copier</>}
    </button>
  );
};

/**
 * Titre d'une famille de sources dans la fenêtre « Gestion des sources distantes »
 * (2026-10-24) : icône en pastille, libellé, nombre en badge, actions à droite.
 */
const SectionTitle: React.FC<{ icon: React.ReactNode; label: string; count: number; accent: string; children?: React.ReactNode }> =
  ({ icon, label, count, accent, children }) => (
  <div className="flex items-center gap-3 mb-3">
    <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${accent}`}>{icon}</span>
    <h3 className="text-white font-semibold text-base">{label}</h3>
    <span className="text-xs font-bold tabular-nums px-2 py-0.5 rounded-full bg-slate-700 text-slate-200 border border-slate-600">{count}</span>
    <span className="flex-1" />
    {children}
  </div>
);

const PTZ_PROTOCOLS: { value: PtzProtocol; label: string }[] = [
  { value: 'onvif',        label: 'ONVIF'         },
  { value: 'visca-ip',     label: 'VISCA-IP'       },
  { value: 'pelco-d',      label: 'Pelco-D'        },
  { value: 'generic-rtsp', label: 'RTSP générique' },
];

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  mode                       : 'add' | 'manager';
  open                       : boolean;
  onClose                    : () => void;
  remoteDevices              : RemoteDevice[];
  remoteConnections          : Map<string, RemoteConnection>;
  serverUrl                  : string;
  // null quand aucune chaîne n'est identifiée : les liens d'appairage n'ont alors
  // aucun sens et aucune écriture ne doit partir. L'onglet « remote » le dit à
  // l'opérateur plutôt que de fabriquer des URL vers nulle part.
  streamPath                 : string | null;
  webRtcPageUrl              : string;
  channelName                : string;
  microserviceUrl            : string;
  onLoadDevices              : () => void;
  onRegisterDevice           : (id: string, label: string, password: string, authMode?: DeviceAuthMode, options?: { silent?: boolean; accessToken?: string }) => Promise<RegisteredRemoteDevice | undefined>;
  onDeleteDevice             : (device_id: string) => void;
  onDisconnectDevice         : (device_id: string) => void;
  /** Conservé pour les appelants : le panneau n'expose plus de bouton « Régénérer ». */
  onRegeneratePassword       : (device_id: string) => void;
  onSetDirector              : (device_id: string, active: boolean) => void;
  /** Registre des devices Réalisateur, tenu par useLiveStreamSetup (cf. toggleDirector). */
  directorDeviceIds          : Set<string>;
  /** Qualité du lien montant, mesurée et rapportée par chaque device. */
  deviceNetworkQuality       : Map<string, { level: number; sample: any }>;
  /**
   * Enregistre une source HLS dans la régie. `opts.autoRetry` sert aux caméras
   * RTMP : leur playlist n'existe pas tant que le cadreur distant n'a pas lancé
   * son encodeur, et sans réessai la source resterait bloquée sur ce premier 404.
   */
  onRegisterXtreamConnection : (url: string, xtream?: XtreamCredentials, label?: string, opts?: { autoRetry?: boolean }) => void;
  sourceAliases              : SourceAliasMap;
  onAliasesChange            : (aliases: SourceAliasMap) => void;
  availableSources           : (VideoSource | null)[];
  hlsProbeStatusMap          : Map<string, HlsProbeStatus>;
  selectedVideoSource        : string;
  deckPreviewSourceId        : string | null;
  onRemoveSource             : (sourceId: string) => void;
  /**
   * Relance d'un flux : le registre jette l'acquisition en cours et la sonde
   * repart. Rendue à l'opérateur sur chaque ligne de source, pour rattraper un
   * flux cassé sans rouvrir la fiche ni recharger la page.
   */
  onReloadSource             : (sourceId: string) => Promise<void>;
  /**
   * Renommage d'affichage d'une source déjà connue de la régie. Le flux n'est pas
   * touché : c'est le seul moyen de voir un alias changer sur le deck et le
   * multiview sans recharger la régie ni couper l'antenne.
   */
  onRenameSource             : (sourceId: string, label: string) => void;
  channelActive              : boolean;
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Clé de stream complète d'une caméra RTMP (« clé?user=…&pass=… »), celle qu'OBS attend.
 *
 * `rtmp_stream_key` n'est renvoyé que par les routes /rtmp-inputs ; la liste des flux de la
 * régie (/hls-streams) ne porte que `rtmp_key` — la clé SEULE, que MediaMTX refuse depuis
 * l'authentification des caméras. L'URL de publication, elle, contient toujours les
 * identifiants : on en tire la clé quand le champ manque.
 */
function rtmpStreamKeyOf(s: { rtmp_stream_key?: string; rtmp_ingest_url?: string; rtmp_key?: string }): string {
  if (s.rtmp_stream_key) return s.rtmp_stream_key;
  const m = /^rtmps?:\/\/[^/]+\/(.+)$/.exec(s.rtmp_ingest_url ?? '');
  return m ? m[1] : (s.rtmp_key ?? '');
}

function validateHlsUrl(url: string): string | null {
  const t = url.trim();
  if (!t) return 'URL requise';
  if (!/^https?:\/\/.+/i.test(t)) return 'Doit commencer par http:// ou https://';
  if (!t.toLowerCase().includes('m3u8')) return 'Flux HLS attendu (.m3u8)';
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────

const ProductionControlRoom_RemoteDevicesPanel: React.FC<Props> = ({
  mode, open, onClose,
  remoteDevices, remoteConnections,
  serverUrl, streamPath: rawStreamPath, webRtcPageUrl, channelName,
  microserviceUrl,
  onLoadDevices, onRegisterDevice, onDeleteDevice, onDisconnectDevice, onSetDirector, directorDeviceIds, deviceNetworkQuality,
  onRegisterXtreamConnection,
  sourceAliases, onAliasesChange,
  availableSources,
  hlsProbeStatusMap,
  selectedVideoSource,
  deckPreviewSourceId,
  onRemoveSource,
  onReloadSource,
  onRenameSource,
  channelActive,
}) => {

  // Le reste du panneau manipule un chemin de chaîne comme une chaîne de caractères.
  // La valeur vide ne sert que de garde-fou de typage : l'onglet « remote » est
  // remplacé par un avertissement quand il n'y a pas de chaîne, et les écritures
  // passent par les rappels de la régie, qui lèvent déjà (requireDeviceStreamPath).
  const streamPath = rawStreamPath ?? '';

  const { showAlert, showConfirm } = useProductionControlRoom();
  // Valeur inutilisée, mais l'abonnement est conservé : le retirer changerait
  // la fréquence de rendu du composant.
  useSourceRegistry();
  const { settings, setSettings } = useMediaStream();
  // Le carnet d'adresses est celui de l'admin connecté (même source que le dashboard).
  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);

  // ── Onglet ───────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<TabId>('remote');

  // ── Remote device ────────────────────────────────────────────────────────
  const [newDevice,     setNewDevice]     = useState({ id: '', password: '', displayName: '' });
  const [newDeviceMode, setNewDeviceMode] = useState<DeviceAuthMode>('uuid');
  // Destinataires des informations de connexion. Le mail n'a plus de bouton propre :
  // il part au moment de l'ajout du device, et seulement s'il y a une adresse.
  const [newDeviceRecipients, setNewDeviceRecipients] = useState<Recipient[]>([]);
  // Le carnet sert deux zones de saisie : celle du bloc d'ajout et celle du modal
  // d'envoi. La cible dit à handleAddressBookSelect où déverser ; le canal, lui,
  // n'a plus à être choisi d'avance — le carnet propose les deux et l'opérateur
  // coche les coordonnées qu'il veut.
  const [addressBookTarget, setAddressBookTarget] = useState<'creation' | 'mail' | 'rtmp' | 'rtmp-resend' | null>(null);
  // RecipientChipInput est piloté par cet état : le vider suffit à effacer les
  // puces, sans le remontage par `key` qu'exigeait EmailChipInput.
  const resetNewDeviceRecipients = () => setNewDeviceRecipients([]);
  const resetRtmpRecipients      = () => { setRtmpRecipients([]); setRtmpSentTo([]); };
  // Token tiré par la régie pour le device en cours de saisie : il part dans le
  // mail avant que le device n'existe, et « Ajouter » créera le device avec lui.
  const newDeviceTokenRef = useRef<string>('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  const [editState,     setEditState]     = useState<EditState | null>(null);
  const [editSaving,    setEditSaving]    = useState(false);
  const [mailState,     setMailState]     = useState<MailState | null>(null);
  const [rtmpMailState, setRtmpMailState] = useState<RtmpMailState | null>(null);
  const [copiedDevice,  setCopiedDevice]  = useState<string | null>(null);
  // Marquage « Réalisateur » d'une source Smartphone/Web. Le clic envoie au
  // device un signal `director` qui lui déverrouille son onglet Réalisateur.
  //
  // Le registre n'est plus local : il vit dans useLiveStreamSetup et descend par
  // `directorDeviceIds`. Ce panneau est monté deux fois dans la ControlBar, et
  // démonté à la fermeture — un état local donnait donc deux badges qui
  // divergeaient, et un rôle en apparence perdu à la réouverture.
  /**
   * Barres de qualité du lien montant d'un device — les mêmes que celles que la
   * personne voit sur son téléphone, et pour cause : c'est SA mesure, transmise.
   *
   * Rendu seulement s'il y a une mesure. Un device connecté depuis moins de
   * quelques secondes n'en a pas encore, et des barres grises se liraient
   * « réseau nul » là où l'on ne sait pas encore.
   */
  const renderDeviceNetwork = (deviceId: string) => {
    const q = deviceNetworkQuality.get(deviceId);
    if (!q) return null;

    const level = q.level as NetworkQualityLevel;

    // Barres pleines en blanc opaque, comme les barres noires du badge device :
    // même intention — trancher sur le fond du badge — inversée parce qu'ici le
    // badge est sombre (`bg-green-500/20` sur slate). Le niveau se lit au NOMBRE
    // de barres, pas à leur teinte.
    return (
      <span
        title={`${QUALITY_LABELS[level]} — ${describeSample(q.sample)}`}
        aria-label={`${QUALITY_LABELS[level]} (${level} sur 4)`}
        role="img"
        className="inline-flex items-end gap-[2px] h-[11px] shrink-0"
      >
        {[1, 2, 3, 4].map(bar => (
          <span
            key={bar}
            style={{
              display     : 'block',
              width       : 3,
              height      : 2 + bar * 2.25,
              borderRadius: 1,
              background  : bar <= level ? 'rgb(255,255,255)' : 'rgba(255,255,255,0.25)',
              transition  : 'background 0.3s',
            }}
          />
        ))}
      </span>
    );
  };

  const toggleDirector = (deviceId: string) => {
    onSetDirector(deviceId, !directorDeviceIds.has(deviceId));
  };
  const passwordCacheRef = useRef<Map<string, string>>(new Map());
  const accessTokenCacheRef = useRef<Map<string, string>>(new Map());

  // ── Xtream ────────────────────────────────────────────────────────────────
  const [xtreamAccounts,     setXtreamAccounts]     = useState<MicroserviceXtreamAccount[]>([]);
  const [xtreamServer,       setXtreamServer]       = useState('http://');
  const [xtreamUser,         setXtreamUser]         = useState('');
  const [xtreamPass,         setXtreamPass]         = useState('');
  const [xtreamLoading,      setXtreamLoading]      = useState(false);
  const [xtreamError,        setXtreamError]        = useState<string | null>(null);
  const [xtreamStreams,      setXtreamStreams]       = useState<XtreamLiveStream[] | null>(null);
  const [showXtreamSelector, setShowXtreamSelector] = useState(false);
  const [selectedStream,     setSelectedStream]     = useState<XtreamLiveStream | null>(null);
  const [xtreamAlias,        setXtreamAlias]        = useState('');
  const [selectedAccountId,  setSelectedAccountId]  = useState('');

  // ── Annuaires du créateur (admin-api, PAS le microservice) ────────────────
  // Contrairement à xtreamAccounts/hlsStreams (propres à CETTE chaîne), ces
  // annuaires regroupent ce qui a déjà été employé sur N'IMPORTE QUELLE
  // chaîne du même créateur — c'est eux qui évitent de retaper une connexion
  // déjà saisie ailleurs. Cf. api_xtreamDirectory.ts / api_hlsDirectory.ts.
  const creatorAdminId = authentication.admin?.id ?? null;
  const [xtreamDirectory,        setXtreamDirectory]        = useState<XtreamDirectoryEntry[]>([]);
  const [selectedDirectoryId,    setSelectedDirectoryId]    = useState('');
  const [hlsDirectory,           setHlsDirectory]           = useState<HlsDirectoryEntry[]>([]);
  const [selectedHlsDirectoryId, setSelectedHlsDirectoryId] = useState('');
  const [selectedHlsStreamId,    setSelectedHlsStreamId]    = useState('');

  const xtreamCredentialsRef = useRef<{
    server   : string;
    username : string;
    password : string;
  } | null>(null);

  // ── v14 : ref pour l'account.id retourné par addXtreamAccount ────────────
  const xtreamAccountIdRef = useRef<string>('');

  // ── HLS ───────────────────────────────────────────────────────────────────
  const [hlsStreams, setHlsStreams] = useState<MicroserviceHlsStream[]>([]);
  const [hlsUrl,     setHlsUrl]     = useState('');
  const [hlsLabel,   setHlsLabel]   = useState('');
  const [hlsError,   setHlsError]   = useState<string | null>(null);
  const [hlsAdding,  setHlsAdding]  = useState(false);

  // ── Caméra RTMP ───────────────────────────────────────────────────────────
  // `justCreated` retient la caméra tout juste créée : c'est le seul moment où
  // l'opérateur a besoin de voir la clé en grand, pour la transmettre. Ensuite
  // elle redevient une ligne de liste comme une autre.
  const [rtmpLabel,       setRtmpLabel]       = useState('');
  const [rtmpAdding,      setRtmpAdding]      = useState(false);
  const [rtmpError,       setRtmpError]       = useState<string | null>(null);
  const [rtmpJustCreated, setRtmpJustCreated] = useState<MicroserviceHlsStream | null>(null);
  // Destinataires du serveur et de la clé qu'on vient de générer. Contrairement à
  // une caméra Smartphone/Web, l'envoi ne peut pas être préparé avant la création :
  // la clé n'existe pas encore, c'est le microservice qui la tire.
  const [rtmpRecipients,  setRtmpRecipients]  = useState<Recipient[]>([]);
  const [rtmpSentTo,      setRtmpSentTo]      = useState<string[]>([]);
  const [copiedValue,     setCopiedValue]     = useState<string | null>(null);
  const [rtmpStatuses,    setRtmpStatuses]    = useState<Map<string, MicroserviceRtmpStatus>>(new Map());
  const [editRtmpState,   setEditRtmpState]   = useState<{ stream: MicroserviceHlsStream; label: string; saving: boolean; error: string | null } | null>(null);

  // ── PTZ ───────────────────────────────────────────────────────────────────
  const [ptzCameras, setPtzCameras] = useState<MicroservicePtzCamera[]>([]);
  const [ptzForm,    setPtzForm]    = useState({
    label: '', protocol: 'onvif' as PtzProtocol,
    host: '', port: '80', username: '', password: '', stream_url: '',
  });
  const [ptzError,   setPtzError]   = useState<string | null>(null);
  const [ptzAdding,  setPtzAdding]  = useState(false);

  // ── Modales d'édition ─────────────────────────────────────────────────────
  const [editXtreamState,        setEditXtreamState]        = useState<EditXtreamState | null>(null);
  const [editXtreamAliasState,   setEditXtreamAliasState]   = useState<EditXtreamAliasState | null>(null);
  const [editHlsState,           setEditHlsState]           = useState<EditHlsState | null>(null);
  const [showEditXtreamSelector, setShowEditXtreamSelector] = useState(false);

  // ── Chargement microservice ───────────────────────────────────────────────

  const loadXtreamAccounts = useCallback(async () => {
    try {
      const data = await listXtreamAccounts(microserviceUrl);
      setXtreamAccounts(Array.isArray(data?.accounts) ? data.accounts : []);
    } catch (err) {
      console.error('[Sources] Erreur chargement comptes Xtream:', err);
      setXtreamAccounts([]);
    }
  }, [microserviceUrl]);

  const loadXtreamDirectory = useCallback(async () => {
    if (creatorAdminId === null) { setXtreamDirectory([]); return; }
    try {
      setXtreamDirectory(await listXtreamDirectory(creatorAdminId));
    } catch (err) {
      console.error('[Sources] Erreur chargement annuaire Xtream:', err);
      setXtreamDirectory([]);
    }
  }, [creatorAdminId]);

  /**
   * Retient un triplet dans l'annuaire du créateur — appelé juste après une
   * connexion Xtream qui vient de faire ses preuves (fetch de flux réussi).
   * Volontairement muet en cas d'échec : l'annuaire est un confort, jamais un
   * chemin bloquant pour ajouter une source.
   */
  const persistToXtreamDirectory = useCallback((creds: { server: string; username: string; password: string }) => {
    if (creatorAdminId === null) return;
    saveXtreamDirectoryEntry(creatorAdminId, creds)
      .then(() => loadXtreamDirectory())
      .catch(err => console.error('[Sources] Erreur persistance annuaire Xtream:', err));
  }, [creatorAdminId, loadXtreamDirectory]);

  const handlePickDirectoryEntry = useCallback((id: string) => {
    setSelectedAccountId('');
    setSelectedDirectoryId(id);
    const entry = xtreamDirectory.find(e => String(e.id) === id);
    if (!entry) return;
    setXtreamServer(entry.server);
    setXtreamUser(entry.username);
    setXtreamPass(entry.password);
    setXtreamError(null);
  }, [xtreamDirectory]);

  const handleDeleteDirectoryEntry = useCallback((id: string) => {
    if (creatorAdminId === null) return;
    deleteXtreamDirectoryEntry(creatorAdminId, Number(id))
      .then(() => {
        setXtreamDirectory(prev => prev.filter(e => String(e.id) !== id));
        setSelectedDirectoryId(prev => prev === id ? '' : prev);
      })
      .catch(err => console.error('[Sources] Erreur suppression annuaire Xtream:', err));
  }, [creatorAdminId]);

  // ── Sélecteur unique « Serveur » — liste à plat, sans distinguer d'où vient
  // chaque entrée (cette chaîne ou une autre) : un compte déjà enregistré ICI
  // peut aussi être recopié dans l'annuaire du créateur (persistToXtreamDirectory),
  // on ne le liste qu'une fois — cf. retour d'Alex sur la répétition Compte
  // enregistré / Nouveau serveur, et sur l'inutilité de nommer les chaînes.
  const xtreamDirectoryOnlyEntries = xtreamDirectory.filter(entry =>
    !xtreamAccounts.some(acc => acc.server === entry.server && acc.username === entry.username)
  );

  const xtreamSourceChoice = selectedAccountId
    ? `account:${selectedAccountId}`
    : selectedDirectoryId
      ? `directory:${selectedDirectoryId}`
      : '';

  const handleXtreamSourceChange = useCallback((value: string) => {
    setXtreamError(null);
    if (value.startsWith('account:')) {
      setSelectedAccountId(value.slice('account:'.length));
      setSelectedDirectoryId('');
    } else if (value.startsWith('directory:')) {
      handlePickDirectoryEntry(value.slice('directory:'.length));
    } else {
      setSelectedAccountId('');
      setSelectedDirectoryId('');
      setXtreamServer('http://');
      setXtreamUser('');
      setXtreamPass('');
    }
  }, [handlePickDirectoryEntry]);

  const loadHlsStreams = useCallback(async () => {
    try {
      const data = await listAllHlsStreams(microserviceUrl);
      setHlsStreams(Array.isArray(data?.streams) ? data.streams : []);
    } catch (err) {
      console.error('[Sources] Erreur chargement flux HLS:', err);
      setHlsStreams([]);
    }
  }, [microserviceUrl]);

  const loadHlsDirectory = useCallback(async () => {
    if (creatorAdminId === null) { setHlsDirectory([]); return; }
    try {
      setHlsDirectory(await listHlsDirectory(creatorAdminId));
    } catch (err) {
      console.error('[Sources] Erreur chargement annuaire HLS:', err);
      setHlsDirectory([]);
    }
  }, [creatorAdminId]);

  /** Même principe que persistToXtreamDirectory, pour les URL HLS libres. */
  const persistToHlsDirectory = useCallback((entry: { url: string; label?: string }) => {
    if (creatorAdminId === null) return;
    saveHlsDirectoryEntry(creatorAdminId, entry)
      .then(() => loadHlsDirectory())
      .catch(err => console.error('[Sources] Erreur persistance annuaire HLS:', err));
  }, [creatorAdminId, loadHlsDirectory]);

  const handleDeleteHlsDirectoryEntry = useCallback((id: string) => {
    if (creatorAdminId === null) return;
    deleteHlsDirectoryEntry(creatorAdminId, Number(id))
      .then(() => {
        setHlsDirectory(prev => prev.filter(e => String(e.id) !== id));
        setSelectedHlsDirectoryId(prev => prev === id ? '' : prev);
      })
      .catch(err => console.error('[Sources] Erreur suppression annuaire HLS:', err));
  }, [creatorAdminId]);

  // Liste à plat des URL déjà connues : les flux libres de CETTE chaîne, puis
  // celles de l'annuaire du créateur qui n'y sont pas déjà — même dédoublonnage
  // que pour Xtream, par URL cette fois.
  const hlsFreeStreams = hlsStreams.filter(s => s.type === 'free');
  const hlsDirectoryOnlyEntries = hlsDirectory.filter(entry =>
    !hlsFreeStreams.some(s => s.url === entry.url)
  );

  const hlsSourceChoice = selectedHlsStreamId
    ? `stream:${selectedHlsStreamId}`
    : selectedHlsDirectoryId
      ? `directory:${selectedHlsDirectoryId}`
      : '';

  const handleHlsSourceChange = useCallback((value: string) => {
    setHlsError(null);
    if (value.startsWith('stream:')) {
      const id = value.slice('stream:'.length);
      setSelectedHlsStreamId(id);
      setSelectedHlsDirectoryId('');
      const stream = hlsStreams.find(s => s.id === id);
      setHlsUrl(stream?.url ?? '');
      setHlsLabel(stream?.label ?? '');
    } else if (value.startsWith('directory:')) {
      const id = value.slice('directory:'.length);
      setSelectedHlsStreamId('');
      setSelectedHlsDirectoryId(id);
      const entry = hlsDirectory.find(e => String(e.id) === id);
      setHlsUrl(entry?.url ?? '');
      setHlsLabel(entry?.label ?? '');
    } else {
      setSelectedHlsStreamId('');
      setSelectedHlsDirectoryId('');
      setHlsUrl('');
      setHlsLabel('');
    }
  }, [hlsStreams, hlsDirectory]);

  /**
   * État de publication des caméras RTMP, relevé auprès de MediaMTX.
   *
   * La sonde HLS ne suffit pas ici : tant que personne ne publie, le path
   * n'existe pas et la playlist répond 404 — indiscernable d'un flux cassé.
   * MediaMTX, lui, sait dire « aucun encodeur connecté ». Le relevé ne tourne
   * que panneau ouvert, et seulement s'il y a au moins une caméra RTMP.
   */
  useEffect(() => {
    if (!open) return;
    const rtmpIds = hlsStreams.filter(s => s.type === 'rtmp').map(s => s.id);
    if (rtmpIds.length === 0) { setRtmpStatuses(new Map()); return; }

    let cancelled = false;
    const refresh = async () => {
      const entries = await Promise.all(rtmpIds.map(async id => {
        try {
          const { status } = await getRtmpInputStatus(microserviceUrl, id);
          return [id, status] as const;
        } catch {
          return [id, { key: '', state: 'unknown' as const }] as const;
        }
      }));
      if (!cancelled) setRtmpStatuses(new Map(entries));
    };

    refresh();
    const timer = setInterval(refresh, 5000);
    return () => { cancelled = true; clearInterval(timer); };
  // hlsStreams change d'identité à chaque rechargement : on ne dépend que des
  // identifiants RTMP, sinon la sonde repartirait de zéro à chaque relevé.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, microserviceUrl, hlsStreams.filter(s => s.type === 'rtmp').map(s => s.id).join(',')]);

  // Ligne dont la relance est en cours : l'icône y tourne jusqu'à ce que la sonde
  // ait tranché. Un seul identifiant suffit — le bouton est désactivé pendant ce
  // temps, on ne relance donc pas deux flux à la fois depuis ce panneau.
  const [reloadingSourceId, setReloadingSourceId] = useState<string | null>(null);

  const handleReloadClick = useCallback(async (sourceId: string) => {
    setReloadingSourceId(sourceId);
    try { await onReloadSource(sourceId); }
    finally { setReloadingSourceId(null); }
  }, [onReloadSource]);

  // Le microservice ne retient d'une source Xtream que l'identifiant numérique de
  // sa chaîne ; le nom vit dans le catalogue du compte. On charge ce catalogue une
  // fois par compte à l'ouverture du panneau — c'est le cache du microservice qui
  // répond, le serveur Xtream n'est pas rappelé — et on garde de quoi nommer
  // chaque ligne.
  const [xtreamChannelNames, setXtreamChannelNames] = useState<Map<string, string>>(new Map());
  const requestedChannelAccountsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    const accountIds = Array.from(new Set(
      hlsStreams
        .filter(st => st.type === 'xtream' && st.xtream_account_id)
        .map(st => st.xtream_account_id as string)
    )).filter(id => !requestedChannelAccountsRef.current.has(id));
    if (accountIds.length === 0) return;

    accountIds.forEach(id => requestedChannelAccountsRef.current.add(id));
    Promise.all(accountIds.map(async (id): Promise<[string, string][]> => {
      // Deux sources pour le même catalogue, dans cet ordre.
      //
      // 1. Le cache du microservice : gratuit, et déjà à jour dans le cas normal.
      // 2. Le serveur Xtream interrogé depuis le navigateur — l'appel qu'utilise
      //    déjà la fenêtre d'édition. Le microservice rend un cache vide quand son
      //    propre refetch a échoué (il joint le serveur autrement que nous), et
      //    dans ce cas la ligne resterait muette alors que le nom est à portée.
      try {
        const { channels } = await getXtreamChannels(microserviceUrl, id);
        if (channels?.length) {
          return channels.map(c => [`${id}:${c.stream_id}`, c.name] as [string, string]);
        }
        console.warn('[Sources] Catalogue Xtream vide côté microservice pour', id, '— repli navigateur');
      } catch (err) {
        console.warn('[Sources] Catalogue Xtream indisponible côté microservice pour', id, err);
      }

      try {
        const { account } = await getXtreamAccount(microserviceUrl, id, true);
        const res = await getLiveStreams({
          server  : account.server,
          username: account.username,
          password: account.password ?? '',
        });
        if (res.code === 1 && res.payload.length > 0) {
          return res.payload.map(c => [`${id}:${c.stream_id}`, c.name] as [string, string]);
        }
        console.warn('[Sources] Catalogue Xtream vide pour', id, ':', res.comment);
      } catch (err) {
        console.warn('[Sources] Catalogue Xtream injoignable pour', id, err);
      }

      // Les deux voies ont échoué : on oublie la tentative pour que la prochaine
      // ouverture du panneau puisse réessayer, plutôt que de rester muet à vie.
      requestedChannelAccountsRef.current.delete(id);
      return [];
    })).then(entries => {
      const flat = entries.flat();
      if (flat.length === 0) return;
      setXtreamChannelNames(prev => {
        const next = new Map(prev);
        flat.forEach(([key, name]) => next.set(key, name));
        return next;
      });
    });
  }, [open, hlsStreams, microserviceUrl]);

  /**
   * Nom de la chaîne désignée par une source Xtream, `null` tant que le catalogue
   * n'a pas répondu. On ne se rabat pas sur l'identifiant numérique : « #1 » ne
   * désigne rien pour l'opérateur, autant ne rien afficher et laisser le nom
   * arriver.
   */
  const xtreamChannelLabel = (stream: MicroserviceHlsStream): string | null => {
    if (stream.xtream_stream_id == null) return null;
    return xtreamChannelNames.get(`${stream.xtream_account_id ?? ''}:${stream.xtream_stream_id}`)
      ?? null;
  };

  /** « kleon@cast.kleonpro.tv/Lovelight TV » — d'où vient le flux, en une ligne. */
  const xtreamSourceSummary = (
    stream : MicroserviceHlsStream,
    account: MicroserviceXtreamAccount | undefined,
  ): string | null => {
    const channel = xtreamChannelLabel(stream);
    if (!account) return channel;
    const origin = `${account.username}@${account.server.replace(/^https?:\/\//, '')}`;
    return channel ? `${origin}/${channel}` : origin;
  };

  const loadPtzCameras = useCallback(async () => {
    try {
      const data = await listPtzCameras(microserviceUrl);
      setPtzCameras(Array.isArray(data?.cameras) ? data.cameras : []);
    } catch (err) {
      console.error('[Sources] Erreur chargement caméras PTZ:', err);
      setPtzCameras([]);
    }
  }, [microserviceUrl]);

  useEffect(() => {
    if (!open) return;
    loadXtreamAccounts();
    loadXtreamDirectory();
    loadHlsStreams();
    loadHlsDirectory();
    loadPtzCameras();
  }, [open, loadXtreamAccounts, loadXtreamDirectory, loadHlsStreams, loadHlsDirectory, loadPtzCameras]);


  useEffect(() => {
    remoteDevices.forEach(d => {
      if (d.password)     passwordCacheRef.current.set(d.device_id, d.password);
      if (d.access_token) accessTokenCacheRef.current.set(d.device_id, d.access_token);
    });
  }, [remoteDevices]);

  const resolvePassword = (device: RemoteDevice): string =>
    device.password || passwordCacheRef.current.get(device.device_id) || '';

  // Les microservices non mis à jour ne renvoient pas auth_mode → mode historique.
  const resolveAuthMode = (device: RemoteDevice): DeviceAuthMode =>
    device.auth_mode ?? 'password';

  const resolveAccessToken = (device: RemoteDevice): string =>
    device.access_token || accessTokenCacheRef.current.get(device.device_id) || '';

  /**
   * Transport média réellement en vigueur pour un appareil, à afficher en badge.
   *
   * Un appareil CONNECTÉ est jugé sur les faits (session WHEP ouverte ou non) :
   * le réglage global ne s'applique qu'à la prochaine connexion, et un repli P2P
   * — bloc `sfu` jamais provisionné, ou path resté injoignable — se décide
   * appareil par appareil sans que le réglage bouge.
   *
   * Un appareil DÉCONNECTÉ n'a pas de transport ; on annonce alors celui qu'il
   * prendrait s'il se connectait maintenant, et le libellé du survol le dit.
   */
  const resolveTransport = (
    device: RemoteDevice,
  ): { mode: 'sfu' | 'p2p'; effective: boolean; reason: string } => {
    const id = device.device_id;
    if (device.is_connected) {
      return isDeviceOnSfu(id)
        ? { mode: 'sfu', effective: true, reason: 'Le flux de cet appareil transite par le serveur (WHIP/WHEP).' }
        : { mode: 'p2p', effective: true, reason: 'Le flux de cet appareil arrive en direct sur la régie (WebRTC pair à pair).' };
    }
    if (settings.deviceTransport !== 'sfu') {
      return { mode: 'p2p', effective: false, reason: 'Transport réglé sur « Direct » : cet appareil se connectera en pair à pair.' };
    }
    if (isSfuUnavailable(id)) {
      return { mode: 'p2p', effective: false, reason: 'Connexion sécurisée indisponible pour cet appareil (flux SFU injoignable) : repli en direct.' };
    }
    if (!getDeviceSfuInfo(id)) {
      return { mode: 'sfu', effective: false, reason: 'Transport réglé sur « Connexion sécurisée » : le path SFU sera provisionné à la connexion.' };
    }
    return { mode: 'sfu', effective: false, reason: 'Transport réglé sur « Connexion sécurisée » : cet appareil publiera sur le serveur.' };
  };

  /**
   * Lien de connexion directe d'un device, quel que soit son mode d'auth
   * ('' si l'élément secret — jeton ou mot de passe — n'est pas connu du front).
   * Même forme que l'URL envoyée par mail, cf. invitationUrlFor.
   */
  const resolveAccessLink = (device: RemoteDevice): string => {
    if (resolveAuthMode(device) === 'uuid') {
      const token = resolveAccessToken(device);
      return token ? `${webRtcPageUrl}/${buildDeviceAccessUuid(streamPath, token)}` : '';
    }
    const password = resolvePassword(device);
    return password
      ? `${webRtcPageUrl}/${streamPath}?user=${device.device_id}&pwd=${password}`
      : '';
  };

  const copyAccessLink = async (device: RemoteDevice) => {
    const link = resolveAccessLink(device);
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedDevice(device.device_id);
      setTimeout(() => setCopiedDevice(c => (c === device.device_id ? null : c)), 5000);
    } catch {
      showAlert(`Copie impossible. Lien :\n\n${link}`, 'normal');
    }
  };

  if (!open) return null;

  // ── Helpers usage source ──────────────────────────────────────────────────

  const findSourceId = (stream: MicroserviceHlsStream): string | null => {
    if (stream.type === 'xtream' && stream.xtream_stream_id != null) {
      const found = availableSources.find(
        vs => vs && vs.type === 'url' && vs.xtream?.streamId === stream.xtream_stream_id
      );
      return found?.id ?? null;
    }
    const found = availableSources.find(
      vs => vs && vs.type === 'url' && vs.externalUrl === stream.url
    );
    return found?.id ?? null;
  };

  const isSourceInUse = (stream: MicroserviceHlsStream): {
    inFinal: boolean;
    inPreview: boolean;
  } => {
    const sourceId = findSourceId(stream);
    if (!sourceId) return { inFinal: false, inPreview: false };
    return {
      inFinal  : selectedVideoSource === sourceId,
      inPreview: deckPreviewSourceId === sourceId,
    };
  };

  // ── Reset complet ─────────────────────────────────────────────────────────

  const resetXtream = () => {
    setXtreamServer('http://');
    setXtreamUser('');
    setXtreamPass('');
    setXtreamStreams(null);
    setSelectedStream(null);
    setXtreamError(null);
    setXtreamAlias('');
    setSelectedAccountId('');
    xtreamCredentialsRef.current = null;
    xtreamAccountIdRef.current   = '';
  };

  const handleClose = () => {
    setNewDevice({ id: '', password: '', displayName: '' });
    resetNewDeviceRecipients();
    newDeviceTokenRef.current = '';
    setEditState(null);
    resetXtream();
    setHlsUrl('');
    setHlsLabel('');
    setHlsError(null);
    setPtzForm({ label: '', protocol: 'onvif', host: '', port: '80', username: '', password: '', stream_url: '' });
    setPtzError(null);
    setEditXtreamState(null);
    setEditHlsState(null);
    setEditRtmpState(null);
    setRtmpLabel('');
    setRtmpError(null);
    setRtmpJustCreated(null);
    resetRtmpRecipients();
    onClose();
  };

  // ── Handlers Remote ───────────────────────────────────────────────────────

  /**
   * Le formulaire de création est-il exploitable en l'état ?
   * En mode identifiant, le mot de passe est obligatoire : il doit figurer dans
   * le mail, qui peut partir avant la création — on ne peut donc pas le laisser
   * générer par le serveur.
   */
  const canRegisterDevice =
    channelActive
    && !isRegistering
    && (newDeviceMode === 'uuid'
          ? !!newDevice.displayName.trim()
          : (!!newDevice.id.trim() && !!newDevice.password.trim()));


  /**
   * Token du device en cours de saisie, tiré ici puis vérifié auprès du
   * microservice. On retire tant qu'il est déjà pris sur cette chaîne — sur
   * 62^5 combinaisons, une seule tentative suffit en pratique.
   */
  const ensureAccessToken = async (): Promise<string> => {
    if (newDeviceTokenRef.current) return newDeviceTokenRef.current;
    for (let attempt = 0; attempt < 8; attempt++) {
      const candidate = generateDeviceAccessToken();
      if (await isDeviceAccessTokenFree(serverUrl, streamPath, candidate)) {
        newDeviceTokenRef.current = candidate;
        return candidate;
      }
    }
    throw new Error("Impossible d'obtenir un token de connexion libre");
  };

  const handleRegister = async () => {
    if (isRegistering) return;

    if (newDeviceMode === 'uuid') {
      // Mode lien : seul le nom d'affichage est saisi, le serveur dérive
      // l'identifiant technique. Le token, lui, vient de la régie — c'est celui
      // qui a pu être expédié par mail avant cette création.
      const displayName = newDevice.displayName.trim();
      if (!displayName) return;
      setIsRegistering(true);
      try {
        const token   = await ensureAccessToken();
        const created = await onRegisterDevice('', displayName, '', 'uuid', { accessToken: token });
        newDeviceTokenRef.current = '';
        await sendCreationInvitations(
          created?.device_id    ?? displayName,
          '',
          created?.access_token ?? token,
          'uuid',
          displayName,
        );
        setNewDevice({ id: '', password: '', displayName: '' });
        resetNewDeviceRecipients();
        if (mode === 'add') handleClose();
      } catch (err: any) {
        showAlert(`Erreur lors de l'enregistrement : ${err.message}`, 'important');
      } finally {
        setIsRegistering(false);
      }
      return;
    }

    if (!newDevice.id.trim() || !newDevice.password.trim()) return;
    setIsRegistering(true);
    const id = newDevice.id.trim();
    passwordCacheRef.current.set(id, newDevice.password.trim());
    try {
      const created = await onRegisterDevice(id, id, newDevice.password, 'password');
      await sendCreationInvitations(
        created?.device_id ?? id,
        created?.password  ?? newDevice.password.trim(),
        '',
        'password',
        id,
      );
      setNewDevice({ id: '', password: '', displayName: '' });
      resetNewDeviceRecipients();
      if (mode === 'add') handleClose();
    } catch (err: any) {
      showAlert(`Erreur lors de l'enregistrement : ${err.message}`, 'important');
    } finally {
      setIsRegistering(false);
    }
  };

  /**
   * Envoi des informations de connexion, déclenché par « Ajouter » — jamais seul.
   * Les informations expédiées sont celles du device qui vient d'être créé, pas
   * celles saisies dans le formulaire : c'est le serveur qui a le dernier mot sur
   * l'identifiant technique et le token.
   *
   * Les deux canaux partent du même geste et sont indépendants : remplir l'un,
   * l'autre, ou les deux. Un échec ne remet pas en cause la création — le device
   * existe, on se contente de le signaler.
   */
  const sendCreationInvitations = async (
    deviceId: string, password: string, accessToken: string, authMode: DeviceAuthMode, alias: string,
  ) => {
    const recipients = newDeviceRecipients.filter(isValidRecipient);

    if (recipients.length === 0) return;

    const webRtcUrl = invitationUrlFor(deviceId, authMode, password, accessToken);

    const { failures } = await sendInvitations({
      recipients,
      deviceId, password, webRtcPageUrl: webRtcUrl, authMode, alias,
    });

    if (failures.length > 0) {
      showAlert(`Device créé, mais l'envoi a échoué pour : ${failures.join(', ')}`, 'important');
    }
  };

  /**
   * Expédition d'un même jeu d'informations sur les deux canaux, un appel par
   * destinataire et tous en parallèle.
   *
   * Le compte rendu est rendu à l'appelant plutôt qu'affiché ici : la création et
   * le modal d'envoi n'en font pas la même chose — l'une prévient par une alerte,
   * l'autre bascule sur son écran de confirmation.
   *
   * `failures` porte le destinataire ET le motif ; `sentTo` ne garde que ceux qui
   * sont passés. Un envoi WhatsApp échoue avec le motif renvoyé par Meta.
   */
  const sendInvitations = async (params: {
    recipients    : Recipient[];
    deviceId      : string;
    password      : string;
    webRtcPageUrl : string;
    authMode      : DeviceAuthMode;
    alias         : string;
  }): Promise<{ sentTo: string[]; failures: string[] }> => {

    const { recipients, deviceId, password, webRtcPageUrl, authMode, alias } = params;

    const commun = { serverUrl, streamPath, deviceId, password, webRtcPageUrl, channelName, authMode, alias };

    const failures: string[] = [];
    const sentTo  : string[] = [];

    // Le compte rendu nomme le destinataire comme l'opérateur l'a lu sur sa puce —
    // le nom du contact quand il vient du carnet, la coordonnée sinon. Ce qui part
    // sur le réseau reste `value`, dans tous les cas.
    await Promise.all(recipients.map(async recipient => {
      const affiche = recipientDisplay(recipient);
      try {
        if (recipient.channel === 'whatsapp') {
          await sendDeviceInvitationWhatsApp({ ...commun, recipientWhatsapp: recipient.value });
        } else {
          await sendDeviceInvitationEmail({ ...commun, recipientEmail: recipient.value });
        }
        sentTo.push(affiche);
      } catch (err: any) {
        failures.push(`${affiche} (${err.message})`);
      }
    }));

    return { sentTo, failures };
  };

  /**
   * Déversement du carnet dans la zone de saisie visée.
   *
   * Le dédoublonnage porte sur le couple canal + coordonnée : la même personne
   * peut figurer deux fois, une par canal, et ce sont bien deux envois.
   */
  const handleAddressBookSelect = (picked: Recipient[]) => {
    if (!addressBookTarget) return;

    const merge = (prev: Recipient[]) => {
      const connus = new Set(prev.map(recipientKey));
      return [...prev, ...picked.filter(r => {
        const cle = recipientKey(r);
        if (connus.has(cle)) { return false; }
        connus.add(cle);
        return true;
      })];
    };

    if      (addressBookTarget === 'mail')        { setMailState(s => s ? { ...s, recipients: merge(s.recipients), error: null } : s); }
    else if (addressBookTarget === 'rtmp')        { setRtmpRecipients(merge); }
    else if (addressBookTarget === 'rtmp-resend') { setRtmpMailState(s => s ? { ...s, recipients: merge(s.recipients), error: null } : s); }
    else                                           { setNewDeviceRecipients(merge); }
  };

  const handleSaveEdit = async () => {
    if (!editState) return;
    setEditSaving(true);
    const { device_id: oldId, newId, password: newPwd, label, authMode } = editState;
    try {
      if (authMode === 'uuid') {
        // Pas de mot de passe ni de renommage technique : seul le nom d'affichage
        // est modifiable, l'identifiant reste la clé du lien déjà distribué.
        await updateRemoteDevice(serverUrl, streamPath, oldId, { label: label.trim() });
      } else {
        await updateRemoteDevice(serverUrl, streamPath, oldId, {
          new_device_id: newId !== oldId ? newId : undefined,
          password     : newPwd || undefined,
        });
        const effectivePwd = newPwd || passwordCacheRef.current.get(oldId) || '';
        if (newId !== oldId) passwordCacheRef.current.delete(oldId);
        if (effectivePwd) passwordCacheRef.current.set(newId !== oldId ? newId : oldId, effectivePwd);
      }
      onLoadDevices();
      setEditState(null);
    } catch (err: any) {
      showAlert(`Erreur : ${err.message}`, 'important');
    } finally {
      setEditSaving(false);
    }
  };

  const openRtmpMailModal = (camera: MicroserviceHlsStream) =>
    setRtmpMailState({ camera, recipients: [], sentTo: [], sending: false, error: null, success: false });

  const openMailModal = (device: RemoteDevice) =>
    setMailState({ device, password: resolvePassword(device), recipients: [], sentTo: [], sending: false, error: null, success: false });

  /** URL envoyée au porteur du device — lien direct en mode « uuid ». */
  const invitationUrlFor = (
    deviceId: string, authMode: DeviceAuthMode, password: string, accessToken: string,
  ): string =>
    authMode === 'uuid'
      ? (accessToken
          ? `${webRtcPageUrl}/${buildDeviceAccessUuid(streamPath, accessToken)}`
          : `${webRtcPageUrl}/${streamPath}`)
      : `${webRtcPageUrl}/${streamPath}?user=${deviceId}&pwd=${password}`;

  const buildInvitationUrl = (device: RemoteDevice, password: string): string =>
    invitationUrlFor(device.device_id, resolveAuthMode(device), password, resolveAccessToken(device));

  /**
   * Envoi depuis le modal — même schéma que sendCreationInvitations : un appel par
   * destinataire, en parallèle, sur les deux canaux, et on ne bascule sur l'écran
   * de confirmation que si au moins un destinataire a été servi. Un échec partiel
   * reste visible sur cet écran plutôt que d'être avalé.
   */
  const handleSendMail = async () => {
    if (!mailState) return;

    const recipients = mailState.recipients.filter(isValidRecipient);

    if (recipients.length === 0) return;

    setMailState(s => s ? { ...s, sending: true, error: null } : s);

    const { device, password } = mailState;
    const authMode      = resolveAuthMode(device);
    const webRtcPageUrl = buildInvitationUrl(device, password);

    const { sentTo, failures } = await sendInvitations({
      recipients,
      deviceId     : device.device_id,
      password     : authMode === 'uuid' ? '' : password,
      webRtcPageUrl,
      authMode,
      alias        : device.label,
    });

    if (sentTo.length === 0) {
      setMailState(s => s ? { ...s, sending: false, error: failures.join(', ') } : s);
      return;
    }
    setMailState(s => s ? {
      ...s,
      sending: false,
      success: true,
      sentTo,
      error  : failures.length > 0 ? `Échec pour : ${failures.join(', ')}` : null,
    } : s);
  };

  // ── Handlers Xtream (création) ────────────────────────────────────────────

  const fetchAndShowStreams = async (creds: { server: string; username: string; password: string }) => {
    setXtreamLoading(true);
    setXtreamError(null);
    setSelectedStream(null);
    setXtreamAlias('');

    const result = await getLiveStreams({
      server  : creds.server,
      username: creds.username,
      password: creds.password,
    });

    setXtreamLoading(false);

    if (result.code !== 1 || !result.payload.length) {
      setXtreamError(result.comment || 'Aucun flux retourné');
      xtreamCredentialsRef.current = null;
      return;
    }

    setXtreamStreams(result.payload);
    setShowXtreamSelector(true);
    persistToXtreamDirectory(creds);

    addXtreamAccount(microserviceUrl, creds)
      .then(({ account }) => {
        setSelectedAccountId(account.id);
        xtreamAccountIdRef.current = account.id;
        return refreshXtreamChannels(microserviceUrl, account.id)
          .then(() => loadXtreamAccounts());
      })
      .catch((err) => {
        console.error('[Xtream] Erreur persistance microservice:', err);
      });
  };

  const handleFetchXtream = async () => {
    if (!xtreamServer.trim() || !xtreamUser.trim() || !xtreamPass.trim()) {
      setXtreamError('Tous les champs sont requis');
      return;
    }
    xtreamCredentialsRef.current = {
      server  : xtreamServer.trim(),
      username: xtreamUser.trim(),
      password: xtreamPass.trim(),
    };
    await fetchAndShowStreams(xtreamCredentialsRef.current);
  };

  const handleFetchExistingAccount = async () => {
    if (!selectedAccountId) {
      setXtreamError('Sélectionner un compte');
      return;
    }
    setXtreamLoading(true);
    setXtreamError(null);
    try {
      const { account } = await getXtreamAccount(microserviceUrl, selectedAccountId, true);
      const creds = {
        server  : account.server,
        username: account.username,
        password: account.password ?? '',
      };
      xtreamCredentialsRef.current = creds;
      xtreamAccountIdRef.current   = selectedAccountId;
      setXtreamLoading(false);
      await fetchAndShowStreams(creds);
    } catch (err: any) {
      setXtreamLoading(false);
      setXtreamError(err.message);
      xtreamCredentialsRef.current = null;
    }
  };

  const handleXtreamStreamPicked = (stream: XtreamLiveStream) => {
    setSelectedStream(stream);
    setXtreamAlias(stream.name);
    setShowXtreamSelector(false);
  };

  const handleAddXtream = () => {
    if (!selectedStream || !xtreamCredentialsRef.current) return;
    const creds = xtreamCredentialsRef.current;
    const effectiveAlias = xtreamAlias.trim() || selectedStream.name;
    const url = resolveLiveStreamUrl(
      { server: creds.server, username: creds.username, password: creds.password },
      selectedStream.stream_id,
    );
    onRegisterXtreamConnection(
      url,
      { server: creds.server, username: creds.username, password: creds.password, streamId: selectedStream.stream_id },
      effectiveAlias,
    );

    const accountId = xtreamAccountIdRef.current || selectedAccountId;
    if (accountId) {
      setSelectedXtreamChannel(microserviceUrl, accountId, {
        stream_id  : selectedStream.stream_id,
        name       : selectedStream.name,
        stream_icon: selectedStream.stream_icon,
      }).catch(() => {});
    }

    loadHlsStreams();
    resetXtream();
    if (mode === 'add') handleClose();
  };



  // ── Handlers HLS (création) ───────────────────────────────────────────────

  const handleAddHls = async () => {
    const err = validateHlsUrl(hlsUrl);
    if (err) { setHlsError(err); return; }
    const label = hlsLabel.trim()
      || hlsUrl.trim().split('/').filter(Boolean).pop()?.replace('.m3u8', '')
      || 'HLS';
    setHlsAdding(true);
    try {
      // Un flux déjà connu de CETTE chaîne (choisi dans le sélecteur) est déjà
      // enregistré sur le microservice : le réajouter dupliquerait l'entrée.
      // On se contente alors de brancher la connexion.
      if (!selectedHlsStreamId) {
        await addHlsStream(microserviceUrl, { url: hlsUrl.trim(), label });
        persistToHlsDirectory({ url: hlsUrl.trim(), label });
        await loadHlsStreams();
        if (creatorAdminId) {
          createAdminActionLog(creatorAdminId, 'HlsSource', 'Creation', 'Created', `Source HLS : ${label}` + (channelName ? ` — Chaîne : ${channelName}` : ''));
        }
      }
      onRegisterXtreamConnection(hlsUrl.trim(), undefined, label);
      setHlsUrl('');
      setHlsLabel('');
      setHlsError(null);
      setSelectedHlsStreamId('');
      setSelectedHlsDirectoryId('');
      if (mode === 'add') handleClose();
    } catch (err: any) {
      setHlsError(err.message);
    } finally {
      setHlsAdding(false);
    }
  };

  // ── Handlers caméra RTMP ──────────────────────────────────────────────────

  /**
   * Copie dans le presse-papier, avec repli.
   *
   * `navigator.clipboard` n'existe qu'en contexte sécurisé ; la régie est en
   * HTTPS, mais un poste servi autrement (essai en clair sur le LAN) perdrait
   * silencieusement le seul geste utile de cet écran — la clé ne se retape pas
   * de tête.
   */
  const copyToClipboard = async (value: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.style.position = 'fixed';
        ta.style.opacity  = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopiedValue(value);
      setTimeout(() => setCopiedValue(c => (c === value ? null : c)), 5000);
    } catch {
      showAlert("Copie impossible — sélectionnez la valeur et copiez-la à la main.", 'important');
    }
  };

  /**
   * Crée une caméra RTMP.
   *
   * La clé n'est PAS fabriquée ici : c'est le microservice de la chaîne qui la
   * tire, parce que lui seul connaît l'UUID de sa chaîne et les clés déjà
   * prises. Le front ne fournit que le nom affiché et la façade publique
   * (`serverUrl`) à partir de laquelle composer les deux URL.
   */
  const handleAddRtmp = async () => {
    setRtmpAdding(true);
    setRtmpError(null);
    // Capturé avant reset : les destinataires saisis dans le formulaire, pas ceux
    // d'un éventuel envoi précédent.
    const recipientsToNotify = rtmpRecipients.filter(isValidRecipient);
    try {
      const { input } = await addRtmpInput(microserviceUrl, {
        label   : rtmpLabel.trim(),
        base_url: serverUrl,
      });
      // La source entre tout de suite dans la régie — donc sur le premier bouton
      // libre du deck — et attend son encodeur au lieu de tomber en erreur.
      onRegisterXtreamConnection(input.url, undefined, input.label, { autoRetry: true });
      await loadHlsStreams();
      if (creatorAdminId) {
        createAdminActionLog(creatorAdminId, 'RtmpInput', 'Creation', 'Created', `Caméra RTMP : ${input.label}` + (channelName ? ` — Chaîne : ${channelName}` : ''));
      }
      setRtmpJustCreated(input);
      setRtmpLabel('');
      resetRtmpRecipients();

      // Envoi immédiat, comme pour un device Smartphone/Web (sendCreationInvitations
      // dans handleRegister) : même geste, la caméra existe déjà donc un échec
      // d'envoi ne remet pas en cause sa création — juste signalé.
      if (recipientsToNotify.length > 0) {
        const commun = {
          channelName   : channelName          ,
          cameraLabel   : input.label          ,
          rtmpServer    : rtmpServerUrl()      ,
          rtmpKey       : rtmpStreamKeyOf(input),
          rtmpIngestUrl : rtmpIngestUrlOf(input),
        };
        const failures: string[] = [];
        const sentTo  : string[] = [];
        await Promise.all(recipientsToNotify.map(async recipient => {
          const affiche = recipientDisplay(recipient);
          try {
            await sendRtmpCameraInvitation({ ...commun, channel: recipient.channel, recipient: recipient.value });
            sentTo.push(affiche);
          } catch (err: any) {
            failures.push(`${affiche} (${err.message})`);
          }
        }));
        setRtmpSentTo(sentTo);
        if (failures.length > 0) { setRtmpError(`Échec pour : ${failures.join(', ')}`); }
      }
    } catch (err: any) {
      setRtmpError(err.message);
    } finally {
      setRtmpAdding(false);
    }
  };

  /**
   * Adresse du serveur RTMP, telle que le cadreur doit la coller dans son encodeur.
   *
   * `serverUrl` est la façade publique de la régie — une URL http(s) : seul son
   * hôte compte ici, le port RTMP est celui de MediaMTX. Si ce n'est pas une URL
   * analysable, on la prend telle quelle plutôt que de composer une adresse fausse.
   */
  const rtmpServerUrl = (): string => {
    let hote = serverUrl;
    try { hote = new URL(serverUrl).hostname; } catch { /* pas une URL : tel quel */ }
    return `rtmps://${hote}:1936/`;
  };

  /**
   * URL complète de publication (serveur + clé), composée ici pour rester en
   * RTMPS : `rtmp_ingest_url` vient du microservice, qui la fabrique en rtmp://…:1935.
   * Sans clé exploitable on retombe sur la valeur du microservice, faute de mieux.
   */
  const rtmpIngestUrlOf = (s: { rtmp_stream_key?: string; rtmp_ingest_url?: string; rtmp_key?: string }): string => {
    const cle = rtmpStreamKeyOf(s);
    return cle ? `${rtmpServerUrl()}${cle}` : (s.rtmp_ingest_url ?? '');
  };

  /**
   * Même envoi que celui déclenché par handleAddRtmp à la création, mais pour
   * une caméra RTMP déjà enregistrée (bouton « Envoyer les infos » de la
   * liste) — cf. RtmpMailState.
   */
  const handleSendRtmpMail = async () => {
    if (!rtmpMailState) return;

    const recipients = rtmpMailState.recipients.filter(isValidRecipient);
    if (recipients.length === 0) return;

    setRtmpMailState(s => s ? { ...s, sending: true, error: null } : s);

    const { camera } = rtmpMailState;
    const commun = {
      channelName   : channelName                 ,
      cameraLabel   : camera.label                ,
      rtmpServer    : rtmpServerUrl()             ,
      rtmpKey       : rtmpStreamKeyOf(camera),
      rtmpIngestUrl : rtmpIngestUrlOf(camera),
    };

    const failures: string[] = [];
    const sentTo  : string[] = [];

    await Promise.all(recipients.map(async recipient => {
      const affiche = recipientDisplay(recipient);
      try {
        await sendRtmpCameraInvitation({ ...commun, channel: recipient.channel, recipient: recipient.value });
        sentTo.push(affiche);
      } catch (err: any) {
        failures.push(`${affiche} (${err.message})`);
      }
    }));

    if (sentTo.length === 0) {
      setRtmpMailState(s => s ? { ...s, sending: false, error: failures.join(', ') } : s);
      return;
    }
    setRtmpMailState(s => s ? {
      ...s,
      sending: false,
      success: true,
      sentTo,
      error  : failures.length > 0 ? `Échec pour : ${failures.join(', ')}` : null,
    } : s);
  };

  const handleSaveEditRtmp = async () => {
    if (!editRtmpState) return;
    const label = editRtmpState.label.trim() || editRtmpState.stream.label;
    setEditRtmpState(s => s ? { ...s, saving: true, error: null } : s);
    try {
      await renameRtmpInput(microserviceUrl, editRtmpState.stream.id, label);
      await loadHlsStreams();
      const sourceId = findSourceId(editRtmpState.stream);
      if (sourceId) onRenameSource(sourceId, label);
      setEditRtmpState(null);
    } catch (err: any) {
      setEditRtmpState(s => s ? { ...s, saving: false, error: err.message } : s);
    }
  };

  // ── Régénération des identifiants d'une caméra RTMP ──────────────────────

  const handleRegenerateRtmp = (stream: MicroserviceHlsStream) => {
    showConfirm(
      `Régénérer les identifiants de "${stream.label}" ? L'ancienne URL sera refusée à toute `
      + `nouvelle connexion (un encodeur déjà connecté n'est pas coupé). Il faudra transmettre `
      + `la nouvelle URL au cadreur.`,
      async () => {
        try {
          await regenerateRtmpInputCredentials(microserviceUrl, stream.id, serverUrl);
          await loadHlsStreams();
          if (creatorAdminId) {
            createAdminActionLog(
              creatorAdminId, 'RtmpInput', 'Modification', 'Updated',
              `Identifiants régénérés — caméra RTMP : ${stream.label}`
                + (channelName ? ` — Chaîne : ${channelName}` : ''),
            );
          }
        } catch (err: any) {
          showAlert(`Erreur régénération : ${err.message}`, 'important');
        }
      },
    );
  };

  // ── Suppression d'un flux HLS/Xtream ────────────────────────────────────

  const handleDeleteStream = (stream: MicroserviceHlsStream) => {
    const usage    = isSourceInUse(stream);
    const sourceId = findSourceId(stream);

    let confirmMessage: string;
    if (usage.inFinal && usage.inPreview) {
      confirmMessage = `"${stream.label}" est actuellement en flux final ET en preview. Supprimer quand même ?`;
    } else if (usage.inFinal) {
      confirmMessage = `"${stream.label}" est actuellement le flux final. Supprimer quand même ?`;
    } else if (usage.inPreview) {
      confirmMessage = `"${stream.label}" est actuellement en preview. Supprimer quand même ?`;
    } else {
      confirmMessage = `Supprimer la source "${stream.label}" ?`;
    }

    showConfirm(confirmMessage, async () => {
      try {
        // Une caméra RTMP passe par sa propre route : /hls-streams la retirerait
        // bien du registre en RAM, mais sans réécrire le fichier — la clé
        // reviendrait au prochain démarrage du microservice.
        if (stream.type === 'rtmp') await deleteRtmpInput(microserviceUrl, stream.id);
        else                        await deleteHlsStream(microserviceUrl, stream.id);
        await loadHlsStreams();
        if (sourceId) onRemoveSource(sourceId);
        // Xtream n'a pas d'« ajout/retrait » distinct de sa connexion (cf.
        // RtmpInput/XtreamSource loggés à la connexion dans LiveStreamManager) :
        // seules les sources HLS libres et RTMP ont un cycle de vie propre.
        if (creatorAdminId && stream.type !== 'xtream') {
          createAdminActionLog(
            creatorAdminId,
            stream.type === 'rtmp' ? 'RtmpInput' : 'HlsSource',
            'Deletion',
            'Deleted',
            (stream.type === 'rtmp' ? `Caméra RTMP : ${stream.label}` : `Source HLS : ${stream.label}`)
              + (channelName ? ` — Chaîne : ${channelName}` : ''),
          );
        }
      } catch (err: any) {
        showAlert(`Erreur suppression : ${err.message}`, 'important');
      }
    });
  };

  // ── Édition d'un flux HLS libre ───────────────────────────────────────────

  const openEditHls = (stream: MicroserviceHlsStream) => {
    setEditHlsState({
      stream,
      url   : stream.url,
      label : stream.label,
      error : null,
      saving: false,
    });
  };

  const handleSaveEditHls = async () => {
    if (!editHlsState) return;
    const urlErr = validateHlsUrl(editHlsState.url);
    if (urlErr) { setEditHlsState(s => s ? { ...s, error: urlErr } : s); return; }

    setEditHlsState(s => s ? { ...s, saving: true, error: null } : s);
    try {
      await updateHlsStream(microserviceUrl, editHlsState.stream.id, {
        url  : editHlsState.url.trim(),
        label: editHlsState.label.trim() || editHlsState.url.trim().split('/').filter(Boolean).pop()?.replace('.m3u8', '') || 'HLS',
      });
      await loadHlsStreams();

      const sourceId = findSourceId(editHlsState.stream);
      if (sourceId) {
        const newLabel = editHlsState.label.trim() || editHlsState.url.trim().split('/').filter(Boolean).pop()?.replace('.m3u8', '') || 'HLS';
        if (editHlsState.url.trim() !== editHlsState.stream.url) {
          // L'adresse a changé : c'est un autre flux, il faut le réacquérir.
          onRegisterXtreamConnection(editHlsState.url.trim(), undefined, newLabel);
          onRemoveSource(sourceId);
        } else {
          // Seul le nom bouge. Réenregistrer ne renommerait rien — handleAddHlsSource
          // laisse intacte une source déjà présente — et couperait le flux pour rien.
          onRenameSource(sourceId, newLabel);
        }
      }

      setEditHlsState(null);
    } catch (err: any) {
      setEditHlsState(s => s ? { ...s, saving: false, error: err.message } : s);
    }
  };

  // ── Édition d'un flux Xtream ──────────────────────────────────────────────

  /** Première étape : l'alias et le rappel de ce que la source désigne. */
  const openEditXtream = (stream: MicroserviceHlsStream) => {
    setEditXtreamAliasState({ stream, alias: stream.label, saving: false, error: null });
  };

  /** Enregistre le seul alias — le flux, lui, se change dans la fenêtre suivante. */
  const handleSaveEditXtreamAlias = async () => {
    if (!editXtreamAliasState) return;
    const { stream } = editXtreamAliasState;
    const alias = editXtreamAliasState.alias.trim()
      || xtreamChannelLabel(stream)
      || stream.label;

    setEditXtreamAliasState(st => st ? { ...st, saving: true, error: null } : st);
    try {
      await updateHlsStream(microserviceUrl, stream.id, { label: alias });
      await loadHlsStreams();
      const sourceId = findSourceId(stream);
      if (sourceId) onRenameSource(sourceId, alias);
      setEditXtreamAliasState(null);
    } catch (err: any) {
      setEditXtreamAliasState(st => st ? { ...st, saving: false, error: err.message } : st);
    }
  };

  /**
   * Seconde étape : le compte et la chaîne. L'alias en cours de saisie la
   * traverse — c'est lui qui partira à l'enregistrement, la fenêtre ne le
   * redemande donc pas.
   */
  const openEditXtreamSource = async (stream: MicroserviceHlsStream, alias: string) => {
    const accounts = xtreamAccounts.length > 0 ? xtreamAccounts : await (async () => {
      try {
        const data = await listXtreamAccounts(microserviceUrl);
        const list = Array.isArray(data?.accounts) ? data.accounts : [];
        setXtreamAccounts(list);
        return list;
      } catch { return xtreamAccounts; }
    })();

    setEditXtreamState({
      stream,
      mode             : accounts.length > 0 ? 'existing' : 'new',
      selectedAccountId: stream.xtream_account_id ?? (accounts[0]?.id ?? ''),
      xtreamServer     : 'http://',
      xtreamUser       : '',
      xtreamPass       : '',
      streams          : null,
      showSelector     : false,
      selectedStream   : null,
      alias,
      loading          : false,
      error            : null,
    });
  };

  const handleEditXtreamFetch = async () => {
    if (!editXtreamState) return;
    setEditXtreamState(s => s ? { ...s, loading: true, error: null } : s);

    let creds: { server: string; username: string; password: string } | null = null;

    if (editXtreamState.mode === 'existing') {
      if (!editXtreamState.selectedAccountId) {
        setEditXtreamState(s => s ? { ...s, loading: false, error: 'Sélectionner un compte' } : s);
        return;
      }
      try {
        const { account } = await getXtreamAccount(microserviceUrl, editXtreamState.selectedAccountId, true);
        creds = { server: account.server, username: account.username, password: account.password ?? '' };
      } catch (err: any) {
        setEditXtreamState(s => s ? { ...s, loading: false, error: err.message } : s);
        return;
      }
    } else {
      const { xtreamServer, xtreamUser, xtreamPass } = editXtreamState;
      if (!xtreamServer.trim() || !xtreamUser.trim() || !xtreamPass.trim()) {
        setEditXtreamState(s => s ? { ...s, loading: false, error: 'Tous les champs sont requis' } : s);
        return;
      }
      creds = { server: xtreamServer.trim(), username: xtreamUser.trim(), password: xtreamPass.trim() };
    }

    const result = await getLiveStreams({ server: creds.server, username: creds.username, password: creds.password });
    setEditXtreamState(s => s ? { ...s, loading: false } : s);

    if (result.code !== 1 || !result.payload.length) {
      setEditXtreamState(s => s ? { ...s, error: result.comment || 'Aucun flux retourné' } : s);
      return;
    }

    if (editXtreamState.mode === 'new') {
      persistToXtreamDirectory(creds);
      try {
        const { account } = await addXtreamAccount(microserviceUrl, creds);
        await refreshXtreamChannels(microserviceUrl, account.id);
        await loadXtreamAccounts();
        setEditXtreamState(s => s ? { ...s, selectedAccountId: account.id } : s);
      } catch {}
    }

    setEditXtreamState(s => s ? { ...s, streams: result.payload, showSelector: true } : s);
    setShowEditXtreamSelector(true);
  };

  const handleEditXtreamStreamPicked = (stream: XtreamLiveStream) => {
    setEditXtreamState(s => s ? {
      ...s,
      selectedStream: stream,
      alias         : s.alias || stream.name,
      showSelector  : false,
    } : s);
    setShowEditXtreamSelector(false);
  };

  const handleSaveEditXtream = async () => {
    if (!editXtreamState?.selectedStream) return;

    const stream       = editXtreamState.stream;
    const newStreamId  = editXtreamState.selectedStream.stream_id;
    const newAlias     = editXtreamState.alias.trim() || editXtreamState.selectedStream.name;
    const newAccountId = editXtreamState.selectedAccountId;
    const oldSourceId  = findSourceId(stream);
    const usage        = isSourceInUse(stream);
    const sameAccount  = newAccountId === stream.xtream_account_id;

    const doSave = async () => {
      if (sameAccount) {
        try {
          const { account } = await getXtreamAccount(microserviceUrl, newAccountId, true);
          const newUrl = resolveLiveStreamUrl(
            { server: account.server, username: account.username, password: account.password ?? '' },
            newStreamId,
          );
          await updateHlsStream(microserviceUrl, stream.id, {
            label             : newAlias,
            url               : newUrl,
            xtream_stream_id  : newStreamId,
            xtream_stream_icon: editXtreamState.selectedStream!.stream_icon,
          });
          await setSelectedXtreamChannel(microserviceUrl, newAccountId, {
            stream_id  : newStreamId,
            name       : newAlias,
            stream_icon: editXtreamState.selectedStream!.stream_icon,
          }).catch(() => {});

          if (oldSourceId) onRemoveSource(oldSourceId);
          onRegisterXtreamConnection(
            newUrl,
            {
              server  : account.server,
              username: account.username,
              password: account.password ?? '',
              streamId: newStreamId,
            },
            newAlias,
          );
        } catch (err: any) {
          showAlert(`Erreur mise à jour : ${err.message}`, 'important');
          return;
        }
      } else {
        try {
          await deleteHlsStream(microserviceUrl, stream.id);
          if (oldSourceId) onRemoveSource(oldSourceId);

          const { account } = await getXtreamAccount(microserviceUrl, newAccountId, true);
          const newUrl = resolveLiveStreamUrl(
            { server: account.server, username: account.username, password: account.password ?? '' },
            newStreamId,
          );
          onRegisterXtreamConnection(
            newUrl,
            {
              server  : account.server,
              username: account.username,
              password: account.password ?? '',
              streamId: newStreamId,
            },
            newAlias,
          );
          await setSelectedXtreamChannel(microserviceUrl, newAccountId, {
            stream_id  : newStreamId,
            name       : newAlias,
            stream_icon: editXtreamState.selectedStream!.stream_icon,
          }).catch(() => {});
        } catch (err: any) {
          showAlert(`Erreur mise à jour : ${err.message}`, 'important');
          return;
        }
      }

      await loadHlsStreams();
      setEditXtreamState(null);
      setEditXtreamAliasState(null);
    };

    if (usage.inFinal || usage.inPreview) {
      const where = [
        usage.inFinal   ? 'flux final' : null,
        usage.inPreview ? 'preview'    : null,
      ].filter(Boolean).join(' et ');
      showConfirm(
        `"${stream.label}" est actuellement utilisée en ${where}. Modifier quand même ?`,
        doSave,
      );
    } else {
      await doSave();
    }
  };

  // ── Handlers PTZ ─────────────────────────────────────────────────────────

  const handleAddPtz = async () => {
    setPtzError(null);
    if (!ptzForm.label.trim() || !ptzForm.host.trim()) {
      setPtzError('Label et hôte requis'); return;
    }
    const port = parseInt(ptzForm.port, 10);
    if (isNaN(port) || port <= 0 || port > 65535) {
      setPtzError('Port invalide'); return;
    }
    setPtzAdding(true);
    try {
      await addPtzCamera(microserviceUrl, {
        label     : ptzForm.label.trim(),
        protocol  : ptzForm.protocol,
        host      : ptzForm.host.trim(),
        port,
        username  : ptzForm.username.trim(),
        password  : ptzForm.password.trim(),
        stream_url: ptzForm.stream_url.trim(),
      });
      await loadPtzCameras();
      setPtzForm({ label: '', protocol: 'onvif', host: '', port: '80', username: '', password: '', stream_url: '' });
      if (mode === 'add') handleClose();
    } catch (err: any) {
      setPtzError(err.message);
    } finally {
      setPtzAdding(false);
    }
  };

  const handleDeletePtz = async (cameraId: string) => {
    try {
      await deletePtzCamera(microserviceUrl, cameraId);
      await loadPtzCameras();
    } catch (err: any) {
      showAlert(`Erreur suppression PTZ : ${err.message}`, 'important');
    }
  };

  // ── Render bloc création ──────────────────────────────────────────────────

  const renderCreationBlock = () => (
    <div className={mode === 'manager' ? 'mb-6 p-4 bg-white/[0.03] rounded-xl border border-white/[0.08]' : ''}>
      <div className="flex gap-1 mb-4 bg-black/[0.28] p-1 rounded-lg border border-white/[0.08]">
        {([
          { id: 'remote', label: 'Smartphone/ Web'  },
          { id: 'xtream', label: 'Xtream'           },
          { id: 'hls',    label: 'URL HLS'          },
          { id: 'rtmp',   label: 'RTMP'             },
          { id: 'ptz',    label: 'Caméra PTZ'       },
        ] as { id: TabId; label: string }[]).map(tab => {
          const disabled = tab.id === 'ptz' && PTZ_TAB_TEMPORAIREMENT_DESACTIVE;
          return (
            <button key={tab.id} onClick={() => { if (!disabled) setActiveTab(tab.id); }}
              disabled={disabled}
              title={disabled ? 'Temporairement indisponible' : undefined}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-semibold transition-colors ${
                disabled
                  ? 'text-slate-500 cursor-not-allowed'
                  : activeTab === tab.id
                    ? 'bg-slate-700 text-white shadow'
                    : 'text-slate-400 hover:text-slate-200'}`}>
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ── Bannière chaîne inactive ── */}
      {!channelActive && (
        <div className="mb-3 px-3 py-2 bg-orange-900/30 border border-orange-500/40 rounded-lg">
          <p className="text-xs text-orange-300 text-center">
            ⚠️ Démarrez la chaîne avant d'ajouter une source externe.
          </p>
        </div>
      )}

      {activeTab === 'remote' && !rawStreamPath && (
        <p className="text-xs text-orange-300 text-center">
          ⚠️ Aucune chaîne identifiée — les smartphones ne peuvent pas être gérés ici.
        </p>
      )}

      {activeTab === 'remote' && !!rawStreamPath && (
        <div className="space-y-3">
          {/* Transport des devices : « Direct » = P2P (device ↔ régie), « Connexion
              sécurisée » = SFU (le device publie en WHIP sur le serveur, la régie lit en
              WHEP) — d'où la latence signalée dans le libellé. Drapeau global persisté dans
              les settings, appliqué aux prochaines connexions seulement. */}
          {SHOW_DEVICE_TRANSPORT_TOGGLE && (
          <div>
            <p className="text-xs font-semibold text-slate-200 mb-1">Méthode de transport du flux vidéo</p>
            <div className="flex gap-1 bg-black/[0.28] p-1 rounded-lg border border-white/[0.08]">
              {([
                { id: 'p2p', label: 'Direct'                        },
                { id: 'sfu', label: 'Connexion sécurisée (latence)' },
              ] as { id: 'p2p' | 'sfu'; label: string }[]).map(t => (
                <button key={t.id} onClick={() => setSettings(prev => ({ ...prev, deviceTransport: t.id }))}
                  className={`flex-1 py-1.5 px-3 rounded text-xs font-semibold transition-colors ${
                    settings.deviceTransport === t.id ? 'bg-[#25c400] text-white' : 'text-slate-400 hover:text-slate-200'}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          )}

          {/* Le RETOUR est une question séparée du montant, et il faut la poser
              séparément : par le serveur, la régie publie son canvas UNE fois
              pour tout le parc ; en direct, elle ouvre une PeerConnection, un
              encodeur et un débit montant PAR téléphone. Le direct reste le seul
              qui fonctionne hors antenne, puisque la voie serveur lit un path
              alimenté par la seule diffusion du programme. */}
          {SHOW_DEVICE_TRANSPORT_TOGGLE && (
          <div>
            <p className="text-xs font-semibold text-slate-200 mb-1">Retour d'antenne vers les appareils</p>
            <div className="flex gap-1 bg-black/[0.28] p-1 rounded-lg border border-white/[0.08]">
              {([
                { id: 'mediamtx', label: 'Par le serveur (1 flux)' },
                { id: 'p2p',      label: 'Direct (1 flux/appareil)' },
              ] as { id: 'p2p' | 'mediamtx'; label: string }[]).map(t => (
                <button key={t.id} onClick={() => setSettings(prev => ({ ...prev, returnTransport: t.id }))}
                  className={`flex-1 py-1.5 px-3 rounded text-xs font-semibold transition-colors ${
                    settings.returnTransport === t.id ? 'bg-[#25c400] text-white' : 'text-slate-400 hover:text-slate-200'}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-1 leading-snug">
              Par le serveur, le retour n'existe que pendant la diffusion — hors antenne,
              chaque appareil bascule de lui-même en direct.
            </p>
          </div>
          )}
          {/* Mode de connexion du porteur : saisie identifiant/mot de passe,
              ou lien direct dont l'UUID porte à la fois la chaîne et le device. */}
          <div className="flex gap-1 bg-black/[0.28] p-1 rounded-lg border border-white/[0.08]">
            {([
              { id: 'password', label: 'Identifiant + mot de passe' },
              { id: 'uuid',     label: 'Lien de connexion'          },
            ] as { id: DeviceAuthMode; label: string }[]).map(m => (
              <button key={m.id} onClick={() => setNewDeviceMode(m.id)}
                className={`flex-1 py-1.5 px-3 rounded text-xs font-semibold transition-colors ${
                  newDeviceMode === m.id ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-slate-200'}`}>
                {m.label}
              </button>
            ))}
          </div>

          {newDeviceMode === 'password' ? (
            <div>
              <div className="flex gap-3">
                <input type="text" placeholder="Identifiant"
                  value={newDevice.id} autoFocus
                  onChange={e => setNewDevice(v => ({ ...v, id: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter' && channelActive) handleRegister(); }}
                  className="flex-1 min-w-0 bg-slate-600 text-white rounded px-3 py-2 border border-slate-500 focus:border-blue-400 outline-none" />
                <input type="text" placeholder="Mot de passe"
                  value={newDevice.password}
                  onChange={e => setNewDevice(v => ({ ...v, password: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter' && channelActive) handleRegister(); }}
                  className="flex-1 min-w-0 bg-slate-600 text-white rounded px-3 py-2 font-mono border border-slate-500 focus:border-blue-400 outline-none" />
              </div>
            </div>
          ) : (
            <div>
              <input type="text" placeholder="Nom d'affichage (ex: Caméra Jean)"
                value={newDevice.displayName} autoFocus
                onChange={e => setNewDevice(v => ({ ...v, displayName: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter' && channelActive) handleRegister(); }}
                className="w-full bg-slate-600 text-white rounded px-3 py-2 border border-slate-500 focus:border-blue-400 outline-none" />
            </div>
          )}
          {/* Destinataires des informations de connexion. L'envoi part au moment de
              l'ajout du device, et seulement si au moins un destinataire est saisi.
              Une seule zone pour les deux canaux : une puce violette part par mail,
              une puce verte par WhatsApp. */}
          <div>
            <label className="block text-xs text-slate-400 mb-1">
              Envoyer les informations de connexion (facultatif) — email ou WhatsApp, Entrée ou virgule pour valider
            </label>
            <div className="flex gap-2 items-start">
              <div className="flex-1 min-w-0">
                <RecipientChipInput value={newDeviceRecipients} onChange={setNewDeviceRecipients} />
              </div>
              <Trigger
                iconName   = "bookUser"
                iconWidth  = {30}
                iconHeight = {30}
                onClick    = {() => setAddressBookTarget('creation')}
              />
            </div>
          </div>

          <div className="flex gap-3 pt-1">
            <button
              onClick={handleRegister}
              disabled={!canRegisterDevice}
              title={!channelActive ? "Démarrez la chaîne avant d'ajouter une source" : undefined}
              className="flex-1 bg-[#6366f1] hover:bg-[#5558e6] border border-[#818cf8] disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded py-2 font-semibold flex items-center justify-center gap-2">
              {isRegistering
                ? <><RefreshCw className="w-4 h-4 animate-spin" /> Enregistrement…</>
                : <><Plus className="w-4 h-4" /> Ajouter</>}
            </button>
            <button onClick={handleClose} className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-slate-300 rounded text-sm">
              Annuler
            </button>
          </div>
        </div>
      )}

      {activeTab === 'xtream' && (
        <div className="space-y-3">
          <p className="text-xs text-slate-400">
            Connectez-vous à un serveur Xtream Codes pour récupérer un flux live.
          </p>

          {(xtreamAccounts.length > 0 || xtreamDirectoryOnlyEntries.length > 0) && (
            <div>
              <label className="block text-xs text-slate-400 mb-1">Serveur déjà utilisé</label>
              <div className="flex gap-2">
                <select
                  value={xtreamSourceChoice}
                  onChange={e => handleXtreamSourceChange(e.target.value)}
                  className="flex-1 bg-slate-600 text-white rounded px-3 py-2 text-sm border border-slate-500 focus:border-pink-400 outline-none"
                >
                  <option value="">+ Nouveau serveur</option>
                  {xtreamAccounts.map(acc => (
                    <option key={`account:${acc.id}`} value={`account:${acc.id}`}>
                      {acc.username} @ {acc.server.replace(/^https?:\/\//, '')}
                    </option>
                  ))}
                  {xtreamDirectoryOnlyEntries.map(entry => (
                    <option key={`directory:${entry.id}`} value={`directory:${entry.id}`}>
                      {entry.label ? `${entry.label} — ` : ''}{entry.username} @ {entry.server.replace(/^https?:\/\//, '')}
                    </option>
                  ))}
                </select>
                {selectedDirectoryId && (
                  <button
                    type="button"
                    onClick={() => handleDeleteDirectoryEntry(selectedDirectoryId)}
                    title="Retirer de l'annuaire"
                    className="px-2 bg-slate-700 hover:bg-red-900/50 text-slate-400 hover:text-red-300 rounded border border-slate-500">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          )}

          {!selectedAccountId && (
            <>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Serveur</label>
                <input type="text" value={xtreamServer} placeholder="http://mon-serveur.com"
                  onChange={e => { setXtreamServer(e.target.value); setXtreamError(null); setSelectedDirectoryId(''); }}
                  className="w-full bg-slate-600 text-white rounded px-3 py-2 text-sm border border-slate-500 focus:border-pink-400 outline-none font-mono" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Identifiant</label>
                  <input type="text" value={xtreamUser} placeholder="Nom d'utilisateur"
                    onChange={e => { setXtreamUser(e.target.value); setXtreamError(null); setSelectedDirectoryId(''); }}
                    className="w-full bg-slate-600 text-white rounded px-3 py-2 text-sm border border-slate-500 focus:border-pink-400 outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Mot de passe</label>
                  <input type="password" value={xtreamPass} placeholder="Mot de passe"
                    onChange={e => { setXtreamPass(e.target.value); setXtreamError(null); setSelectedDirectoryId(''); }}
                    className="w-full bg-slate-600 text-white rounded px-3 py-2 text-sm border border-slate-500 focus:border-pink-400 outline-none" />
                </div>
              </div>
            </>
          )}

          {xtreamError && <p className="text-red-400 text-xs">❌ {xtreamError}</p>}

          <button
            onClick={selectedAccountId ? handleFetchExistingAccount : handleFetchXtream}
            disabled={xtreamLoading}
            className="w-full py-2 bg-pink-600/20 hover:bg-pink-600/40 disabled:opacity-50 disabled:cursor-not-allowed text-pink-300 border border-pink-500/40 rounded text-sm font-semibold flex items-center justify-center gap-2">
            {xtreamLoading
              ? <><RefreshCw className="w-4 h-4 animate-spin" /> Chargement…</>
              : 'Récupérer les flux'}
          </button>

          {selectedStream && (
            <>
              <div className="p-3 bg-green-900/20 border border-green-600/40 rounded-lg">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <p className="text-xs text-green-400 font-semibold uppercase tracking-wide shrink-0">Flux sélectionné</p>
                    <p className="text-sm text-white font-mono truncate">{selectedStream.name}</p>
                  </div>
                  <button onClick={() => setShowXtreamSelector(true)}
                    className="shrink-0 text-xs text-slate-400 hover:text-slate-200 underline">
                    Changer
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Alias affiché dans le deck</label>
                <input type="text" value={xtreamAlias}
                  onChange={e => setXtreamAlias(e.target.value)}
                  placeholder={selectedStream.name}
                  className="w-full bg-slate-600 text-white rounded px-3 py-1.5 text-sm border border-slate-500 focus:border-green-400 outline-none font-mono" />
              </div>
            </>
          )}

          <div className="flex gap-3 pt-2 border-t border-slate-600">
            <button
              onClick={handleAddXtream}
              disabled={!selectedStream || !xtreamCredentialsRef.current || !channelActive}
              title={!channelActive ? "Démarrez la chaîne avant d'ajouter une source" : undefined}
              className="flex-1 py-2 bg-[#6366f1] hover:bg-[#5558e6] border border-[#818cf8] disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded font-semibold text-sm flex items-center justify-center gap-2">
              <Plus className="w-4 h-4" /> Ajouter
            </button>
            <button onClick={handleClose} className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-slate-300 rounded text-sm flex items-center gap-1">
              <X className="w-4 h-4" /> Annuler
            </button>
          </div>
        </div>
      )}

      {activeTab === 'hls' && (
        <div className="space-y-3">
          <p className="text-xs text-slate-400">Ajoutez directement un flux HLS depuis une URL m3u8.</p>

          {(hlsFreeStreams.length > 0 || hlsDirectoryOnlyEntries.length > 0) && (
            <div>
              <label className="block text-xs text-slate-400 mb-1">URL déjà utilisée</label>
              <div className="flex gap-2">
                <select
                  value={hlsSourceChoice}
                  onChange={e => handleHlsSourceChange(e.target.value)}
                  className="flex-1 bg-slate-600 text-white rounded px-3 py-2 text-sm border border-slate-500 focus:border-cyan-400 outline-none"
                >
                  <option value="">+ Nouvelle URL</option>
                  {hlsFreeStreams.map(s => (
                    <option key={`stream:${s.id}`} value={`stream:${s.id}`}>{s.label}</option>
                  ))}
                  {hlsDirectoryOnlyEntries.map(entry => (
                    <option key={`directory:${entry.id}`} value={`directory:${entry.id}`}>
                      {entry.label || entry.url}
                    </option>
                  ))}
                </select>
                {selectedHlsDirectoryId && (
                  <button
                    type="button"
                    onClick={() => handleDeleteHlsDirectoryEntry(selectedHlsDirectoryId)}
                    title="Retirer de l'annuaire"
                    className="px-2 bg-slate-700 hover:bg-red-900/50 text-slate-400 hover:text-red-300 rounded border border-slate-500">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs text-slate-400 mb-1">Nom affiché (optionnel)</label>
            <input type="text" value={hlsLabel} placeholder="Mon flux"
              onChange={e => { setHlsLabel(e.target.value); setSelectedHlsStreamId(''); setSelectedHlsDirectoryId(''); }}
              disabled={!!selectedHlsStreamId}
              className="w-full bg-slate-600 text-white rounded px-3 py-2 text-sm border border-slate-500 focus:border-cyan-400 outline-none disabled:opacity-50" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">URL</label>
            <input type="text" value={hlsUrl} placeholder="https://exemple.com/flux/index.m3u8"
              autoFocus={activeTab === 'hls'}
              onChange={e => { setHlsUrl(e.target.value); setHlsError(null); setSelectedHlsStreamId(''); setSelectedHlsDirectoryId(''); }}
              onKeyDown={e => { if (e.key === 'Enter' && channelActive) handleAddHls(); }}
              disabled={!!selectedHlsStreamId}
              className={`w-full bg-slate-600 text-white rounded px-3 py-2 text-sm border outline-none font-mono disabled:opacity-50 ${
                hlsError ? 'border-orange-500 focus:border-orange-400' : 'border-slate-500 focus:border-cyan-400'}`} />
            {hlsError && <p className="text-orange-400 text-xs mt-1">❌ {hlsError}</p>}
          </div>
          <div className="flex gap-3 pt-2 border-t border-slate-600">
            <button
              onClick={handleAddHls}
              disabled={hlsAdding || !channelActive}
              title={!channelActive ? "Démarrez la chaîne avant d'ajouter une source" : undefined}
              className="flex-1 py-2 bg-[#6366f1] hover:bg-[#5558e6] border border-[#818cf8] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded font-semibold text-sm flex items-center justify-center gap-2">
              {hlsAdding ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Ajouter
            </button>
            <button onClick={handleClose} className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-slate-300 rounded text-sm flex items-center gap-1">
              <X className="w-4 h-4" /> Annuler
            </button>
          </div>
        </div>
      )}

      {activeTab === 'rtmp' && (
        <div className="space-y-3">
          <p className="text-xs text-slate-400">
            Une caméra RTMP se publie <strong className="text-slate-300">vers</strong> la régie : on
            génère ici une clé, le cadreur la colle dans son encodeur (OBS, boîtier, téléphone),
            et le flux apparaît sur un bouton libre du deck dès qu'il pousse.
          </p>

          {rtmpJustCreated ? (
            <div className="space-y-3">
              <div className="px-3 py-2 bg-emerald-900/30 border border-emerald-500/40 rounded-lg">
                <p className="text-xs text-emerald-300">
                  ✅ « {rtmpJustCreated.label} » créée. Transmettez ces deux valeurs au cadreur —
                  elles ne se retrouvent pas de tête.
                </p>
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1">Serveur (OBS : « Serveur »)</label>
                <div className="flex gap-2">
                  <input readOnly value={rtmpServerUrl()}
                    className="flex-1 bg-slate-900 text-emerald-300 rounded px-3 py-2 text-sm border border-slate-600 font-mono select-all" />
                  <button
                    onClick={() => copyToClipboard(rtmpServerUrl())}
                    title="Copier"
                    className="px-3 py-2 bg-slate-600 hover:bg-slate-500 text-slate-200 rounded border border-slate-500">
                    {copiedValue === (rtmpServerUrl()) ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1">Clé de stream (OBS : « Clé de stream »)</label>
                <div className="flex gap-2">
                  <input readOnly value={rtmpStreamKeyOf(rtmpJustCreated)}
                    className="flex-1 bg-slate-900 text-cyan-300 rounded px-3 py-2 text-sm border border-slate-600 font-mono select-all" />
                  <button
                    onClick={() => copyToClipboard(rtmpStreamKeyOf(rtmpJustCreated))}
                    title="Copier"
                    className="px-3 py-2 bg-slate-600 hover:bg-slate-500 text-slate-200 rounded border border-slate-500">
                    {copiedValue === (rtmpStreamKeyOf(rtmpJustCreated)) ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1">
                  URL complète (encodeurs qui ne séparent pas serveur et clé)
                </label>
                <div className="flex gap-2">
                  <input readOnly value={rtmpIngestUrlOf(rtmpJustCreated)}
                    className="flex-1 bg-slate-900 text-slate-300 rounded px-3 py-2 text-xs border border-slate-600 font-mono select-all" />
                  <button
                    onClick={() => copyToClipboard(rtmpIngestUrlOf(rtmpJustCreated))}
                    title="Copier"
                    className="px-3 py-2 bg-slate-600 hover:bg-slate-500 text-slate-200 rounded border border-slate-500">
                    {copiedValue === (rtmpIngestUrlOf(rtmpJustCreated)) ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {copiedValue && <p className="text-xs text-emerald-400">Copié dans le presse-papier.</p>}

              {/* L'envoi part déjà au moment de « Générer la clé » (mêmes destinataires
                  que le formulaire) — ce qui suit n'est qu'un compte rendu, jamais une
                  deuxième saisie. Pour renvoyer plus tard, cf. l'icône ✉️ de la liste. */}
              {(rtmpSentTo.length > 0 || rtmpError) && (
                <div className="pt-2 border-t border-slate-600">
                  {rtmpSentTo.length > 0 && (
                    <p className="text-xs text-emerald-400">
                      ✅ Informations envoyées à <strong className="text-white">{rtmpSentTo.join(', ')}</strong>.
                    </p>
                  )}
                  {rtmpError && <p className="text-orange-400 text-xs mt-1">❌ {rtmpError}</p>}
                </div>
              )}

              <p className="text-xs text-slate-400">
                Le bouton du deck reste noir tant que l'encodeur n'a pas démarré : c'est normal,
                la source réessaie toute seule.
              </p>

              <div className="flex gap-3 pt-2 border-t border-slate-600">
                <button onClick={() => { setRtmpJustCreated(null); resetRtmpRecipients(); setRtmpError(null); }}
                  className="flex-1 py-2 bg-slate-600 hover:bg-slate-500 text-slate-200 rounded font-semibold text-sm flex items-center justify-center gap-2">
                  <Plus className="w-4 h-4" /> Ajouter une autre source
                </button>
                <button onClick={handleClose}
                  className="px-4 py-2 bg-[#6366f1] hover:bg-[#5558e6] border border-[#818cf8] text-white rounded text-sm">
                  Terminé
                </button>
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Nom affiché (optionnel)</label>
                <input type="text" value={rtmpLabel} placeholder="Caméra Panasonic 01"
                  autoFocus={activeTab === 'rtmp'}
                  onChange={e => { setRtmpLabel(e.target.value); setRtmpError(null); }}
                  onKeyDown={e => { if (e.key === 'Enter' && channelActive && !rtmpAdding) handleAddRtmp(); }}
                  className="w-full bg-slate-600 text-white rounded px-3 py-2 text-sm border border-slate-500 focus:border-cyan-400 outline-none" />
              </div>
              {/* Même geste que pour un device Smartphone/Web : les destinataires sont
                  saisis ici, et l'envoi part avec la création — pas dans un second temps.
                  La clé n'existe pas encore, mais handleAddRtmp l'attend avant d'envoyer. */}
              <div>
                <label className="block text-xs text-slate-400 mb-1">
                  Envoyer les informations de publication (facultatif) — email ou WhatsApp, Entrée ou virgule pour valider
                </label>
                <div className="flex gap-2 items-start">
                  <div className="flex-1 min-w-0">
                    <RecipientChipInput value={rtmpRecipients} onChange={setRtmpRecipients} />
                  </div>
                  <Trigger
                    iconName   = "bookUser"
                    iconWidth  = {30}
                    iconHeight = {30}
                    onClick    = {() => setAddressBookTarget('rtmp')}
                  />
                </div>
              </div>
              {rtmpError && <p className="text-orange-400 text-xs">❌ {rtmpError}</p>}
              <div className="flex gap-3 pt-2 border-t border-slate-600">
                <button
                  onClick={handleAddRtmp}
                  disabled={rtmpAdding || !channelActive}
                  title={!channelActive ? "Démarrez la chaîne avant d'ajouter une source" : undefined}
                  className="flex-1 py-2 bg-[#6366f1] hover:bg-[#5558e6] border border-[#818cf8] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded font-semibold text-sm flex items-center justify-center gap-2">
                  {rtmpAdding ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Radio className="w-4 h-4" />} Générer la clé
                </button>
                <button onClick={handleClose} className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-slate-300 rounded text-sm flex items-center gap-1">
                  <X className="w-4 h-4" /> Annuler
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'ptz' && (
        <div className="space-y-3">
          <p className="text-xs text-slate-400">Caméra PTZ persistée dans le microservice.</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs text-slate-400 mb-1">Label</label>
              <input type="text" value={ptzForm.label} placeholder="Caméra Scène"
                onChange={e => setPtzForm(f => ({ ...f, label: e.target.value }))}
                className="w-full bg-slate-600 text-white rounded px-3 py-2 text-sm border border-slate-500 focus:border-violet-400 outline-none" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Protocole</label>
              <select value={ptzForm.protocol}
                onChange={e => setPtzForm(f => ({ ...f, protocol: e.target.value as PtzProtocol }))}
                className="w-full bg-slate-600 text-white rounded px-3 py-2 text-sm border border-slate-500 focus:border-violet-400 outline-none">
                {PTZ_PROTOCOLS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Port</label>
              <input type="number" value={ptzForm.port}
                onChange={e => setPtzForm(f => ({ ...f, port: e.target.value }))}
                className="w-full bg-slate-600 text-white rounded px-3 py-2 text-sm border border-slate-500 focus:border-violet-400 outline-none" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-slate-400 mb-1">Hôte / IP</label>
              <input type="text" value={ptzForm.host} placeholder="192.168.1.50"
                onChange={e => setPtzForm(f => ({ ...f, host: e.target.value }))}
                className="w-full bg-slate-600 text-white rounded px-3 py-2 text-sm border border-slate-500 focus:border-violet-400 outline-none font-mono" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Utilisateur</label>
              <input type="text" value={ptzForm.username}
                onChange={e => setPtzForm(f => ({ ...f, username: e.target.value }))}
                className="w-full bg-slate-600 text-white rounded px-3 py-2 text-sm border border-slate-500 focus:border-violet-400 outline-none" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Mot de passe</label>
              <input type="password" value={ptzForm.password}
                onChange={e => setPtzForm(f => ({ ...f, password: e.target.value }))}
                className="w-full bg-slate-600 text-white rounded px-3 py-2 text-sm border border-slate-500 focus:border-violet-400 outline-none" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-slate-400 mb-1">URL RTSP (optionnel — auto si vide)</label>
              <input type="text" value={ptzForm.stream_url} placeholder="rtsp://..."
                onChange={e => setPtzForm(f => ({ ...f, stream_url: e.target.value }))}
                className="w-full bg-slate-600 text-white rounded px-3 py-2 text-sm border border-slate-500 focus:border-violet-400 outline-none font-mono" />
            </div>
          </div>
          {ptzError && <p className="text-orange-400 text-xs">❌ {ptzError}</p>}
          <div className="flex gap-3 pt-2 border-t border-slate-600">
            <button
              onClick={handleAddPtz}
              disabled={ptzAdding || !channelActive}
              title={!channelActive ? "Démarrez la chaîne avant d'ajouter une source" : undefined}
              className="flex-1 py-2 bg-[#6366f1] hover:bg-[#5558e6] border border-[#818cf8] disabled:opacity-50 text-white rounded font-semibold text-sm flex items-center justify-center gap-2">
              {ptzAdding ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Ajouter
            </button>
            <button onClick={handleClose} className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-slate-300 rounded text-sm flex items-center gap-1">
              <X className="w-4 h-4" /> Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // ── Listes manager ────────────────────────────────────────────────────────

  const renderXtreamList = () => {
    const xtreamStreamsList = hlsStreams.filter(s => s.type === 'xtream');
    return (
      <div className="mb-6">
        <SectionTitle icon={<Tv className="w-4 h-4" />} label="Sources Xtream Code" count={xtreamStreamsList.length} accent="bg-sky-500/15 text-sky-300" />
        {xtreamStreamsList.length === 0
          ? <p className="text-slate-400 text-sm italic text-center py-4">Aucune source Xtream enregistrée</p>
          : <div className="space-y-2">
              {xtreamStreamsList.map(s => {
                const account = xtreamAccounts.find(a => a.id === s.xtream_account_id);
                const source  = availableSources.find(
                  vs => vs && vs.xtream?.streamId === s.xtream_stream_id
                );
                const probeStatus = source ? hlsProbeStatusMap.get(source.id) : undefined;
                const summary     = xtreamSourceSummary(s, account);

                const statusDot = probeStatus === 'ok'
                  ? { color: '#4ade80', label: 'Actif'      }
                  : probeStatus === 'probing'
                  ? { color: '#facc15', label: 'Connexion…' }
                  : probeStatus === 'failed'
                  ? { color: '#f87171', label: 'Inactif'    }
                  : { color: '#64748b', label: 'Inconnu'    };

                const { inFinal, inPreview } = isSourceInUse(s);
                const usageBadge = inFinal
                  ? { label: '● Final',   color: '#f43f5e' }
                  : inPreview
                  ? { label: '● Preview', color: '#f59e0b' }
                  : null;

                return (
                  <div key={s.id} className="bg-white/[0.03] border border-white/[0.08] rounded-lg p-3">
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusDot.color, flexShrink: 0, display: 'inline-block' }} />
                          <p className="text-white font-medium text-sm truncate">{s.label}</p>
                          <span style={{ fontSize: 11, color: statusDot.color, fontStyle: 'italic', flexShrink: 0 }}>
                            {statusDot.label}
                          </span>
                          {usageBadge && (
                            <span style={{ fontSize: 11, color: usageBadge.color, fontWeight: 700, flexShrink: 0 }}>
                              {usageBadge.label}
                            </span>
                          )}
                        </div>
                        {/* Le libellé en gras est l'alias, choisi par l'opérateur : sans
                            le compte et le nom de la chaîne, rien ne dit ce que la
                            source diffuse réellement. */}
                        {summary && (
                          <p className="text-xs text-pink-400/80 font-mono mt-0.5 truncate" title={summary}>
                            {summary}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <button
                          onClick={() => source && handleReloadClick(source.id)}
                          disabled={!source || reloadingSourceId === source.id}
                          title={source ? 'Relancer le flux' : 'Flux absent de la régie'}
                          className="px-2 py-1 bg-cyan-600/30 hover:bg-cyan-600/60 text-cyan-300 text-xs rounded border border-cyan-500/40 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <RefreshCw className={`w-3 h-3 ${source && reloadingSourceId === source.id ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                          onClick={() => openEditXtream(s)}
                          title="Modifier"
                          className="px-2 py-1 bg-indigo-600/30 hover:bg-indigo-600/60 text-indigo-300 text-xs rounded border border-indigo-500/40"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => handleDeleteStream(s)}
                          className="px-2 py-1 bg-red-600/30 hover:bg-red-600/60 text-red-300 text-xs rounded border border-red-500/40"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
        }
      </div>
    );
  };

  const renderHlsList = () => {
    const freeStreams = hlsStreams.filter(s => s.type === 'free');
    return (
      <div className="mb-6">
        <SectionTitle icon={<Globe className="w-4 h-4" />} label="Sources HLS" count={freeStreams.length} accent="bg-cyan-500/15 text-cyan-300" />
        {freeStreams.length === 0
          ? <p className="text-slate-400 text-sm italic text-center py-4">Aucune source HLS enregistrée</p>
          : <div className="space-y-2">
              {freeStreams.map(s => {
                // La ligne vient du microservice, la source de la régie : c'est
                // l'URL qui les rapproche, seul point commun entre les deux.
                const source = availableSources.find(vs => vs && vs.externalUrl === s.url);
                const { inFinal, inPreview } = isSourceInUse(s);
                const usageBadge = inFinal
                  ? { label: '● Final',   color: '#f43f5e' }
                  : inPreview
                  ? { label: '● Preview', color: '#f59e0b' }
                  : null;
                return (
                  <div key={s.id} className="bg-white/[0.03] border border-white/[0.08] rounded-lg p-3">
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                          <p className="text-white font-medium text-sm truncate">{s.label}</p>
                          {usageBadge && (
                            <span style={{ fontSize: 11, color: usageBadge.color, fontWeight: 700, flexShrink: 0 }}>
                              {usageBadge.label}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-cyan-400/80 font-mono mt-0.5 truncate">{s.url}</p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <button
                          onClick={() => source && handleReloadClick(source.id)}
                          disabled={!source || reloadingSourceId === source.id}
                          title={source ? 'Relancer le flux' : 'Flux absent de la régie'}
                          className="px-2 py-1 bg-cyan-600/30 hover:bg-cyan-600/60 text-cyan-300 text-xs rounded border border-cyan-500/40 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <RefreshCw className={`w-3 h-3 ${source && reloadingSourceId === source.id ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                          onClick={() => openEditHls(s)}
                          title="Modifier"
                          className="px-2 py-1 bg-indigo-600/30 hover:bg-indigo-600/60 text-indigo-300 text-xs rounded border border-indigo-500/40"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => handleDeleteStream(s)}
                          className="px-2 py-1 bg-red-600/30 hover:bg-red-600/60 text-red-300 text-xs rounded border border-red-500/40"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
        }
      </div>
    );
  };

  const renderRtmpList = () => {
    const rtmpStreams = hlsStreams.filter(s => s.type === 'rtmp');
    return (
      <div className="mb-6">
        <SectionTitle icon={<Radio className="w-4 h-4" />} label="Caméras RTMP" count={rtmpStreams.length} accent="bg-rose-500/15 text-rose-300" />
        {rtmpStreams.length === 0
          ? <p className="text-slate-400 text-sm italic text-center py-4">Aucune caméra RTMP enregistrée</p>
          : <div className="space-y-2">
              {rtmpStreams.map(s => {
                const source = availableSources.find(vs => vs && vs.externalUrl === s.url);
                const status = rtmpStatuses.get(s.id);

                // L'état vient de MediaMTX, pas de la sonde HLS : « en attente »
                // dit qu'aucun encodeur n'est connecté, ce qu'un 404 de playlist
                // ne permet pas de distinguer d'un flux cassé.
                const statusDot = status?.state === 'live'
                  ? { color: '#4ade80', label: 'En direct'  }
                  : status?.state === 'waiting'
                  ? { color: '#facc15', label: 'En attente' }
                  : { color: '#64748b', label: 'Inconnu'    };

                const { inFinal, inPreview } = isSourceInUse(s);
                const usageBadge = inFinal
                  ? { label: '● Final',   color: '#f43f5e' }
                  : inPreview
                  ? { label: '● Preview', color: '#f59e0b' }
                  : null;

                return (
                  <div key={s.id} className="bg-white/[0.03] border border-white/[0.08] rounded-lg p-3">
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusDot.color, flexShrink: 0, display: 'inline-block' }} />
                          <Radio className="w-3 h-3 text-emerald-400 shrink-0" />
                          <p className="text-white font-medium text-sm truncate">{s.label}</p>
                          <span style={{ fontSize: 11, color: statusDot.color, fontStyle: 'italic', flexShrink: 0 }}>
                            {statusDot.label}
                          </span>
                          {usageBadge && (
                            <span style={{ fontSize: 11, color: usageBadge.color, fontWeight: 700, flexShrink: 0 }}>
                              {usageBadge.label}
                            </span>
                          )}
                        </div>
                        {/* La clé EST la caméra : c'est la seule valeur que
                            l'opérateur a besoin de relire ici, pour la
                            retransmettre à un cadreur qui l'a perdue. */}
                        <p className="text-xs text-emerald-400/80 font-mono mt-0.5 truncate" title={rtmpIngestUrlOf(s)}>
                          {rtmpIngestUrlOf(s)}
                        </p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <button
                          onClick={() => copyToClipboard(rtmpIngestUrlOf(s))}
                          title="Copier l'URL de publication"
                          className="px-2 py-1 bg-emerald-600/30 hover:bg-emerald-600/60 text-emerald-300 text-xs rounded border border-emerald-500/40"
                        >
                          {copiedValue === rtmpIngestUrlOf(s) ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                        </button>
                        <button
                          onClick={() => source && handleReloadClick(source.id)}
                          disabled={!source || reloadingSourceId === source.id}
                          title={source ? 'Relancer le flux' : 'Flux absent de la régie'}
                          className="px-2 py-1 bg-cyan-600/30 hover:bg-cyan-600/60 text-cyan-300 text-xs rounded border border-cyan-500/40 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <RefreshCw className={`w-3 h-3 ${source && reloadingSourceId === source.id ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                          onClick={() => setEditRtmpState({ stream: s, label: s.label, saving: false, error: null })}
                          title="Renommer"
                          className="px-2 py-1 bg-indigo-600/30 hover:bg-indigo-600/60 text-indigo-300 text-xs rounded border border-indigo-500/40"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => handleRegenerateRtmp(s)}
                          title="Régénérer les identifiants (l'ancienne URL est refusée)"
                          className="px-2 py-1 bg-amber-600/30 hover:bg-amber-600/60 text-amber-300 text-xs rounded border border-amber-500/40"
                        >
                          <KeyRound className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => openRtmpMailModal(s)}
                          title="Envoyer les infos (email ou WhatsApp)"
                          className="px-2 py-1 bg-emerald-600/30 hover:bg-emerald-600/60 text-emerald-300 text-xs rounded border border-emerald-500/40"
                        >
                          <Mail className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => handleDeleteStream(s)}
                          className="px-2 py-1 bg-red-600/30 hover:bg-red-600/60 text-red-300 text-xs rounded border border-red-500/40"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
        }
      </div>
    );
  };

  const renderPtzList = () => (
    <div className="mb-6">
      <SectionTitle icon={<Video className="w-4 h-4" />} label="Caméras PTZ" count={ptzCameras.length} accent="bg-amber-500/15 text-amber-300" />
      {ptzCameras.length === 0
        ? <p className="text-slate-400 text-sm italic text-center py-4">Aucune caméra PTZ enregistrée</p>
        : <div className="space-y-2">
            {ptzCameras.map(cam => (
              <div key={cam.id} className="bg-white/[0.03] border border-white/[0.08] rounded-lg p-3">
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Video className="w-3 h-3 text-violet-400 shrink-0" />
                      <p className="text-white font-medium text-sm truncate">{cam.label}</p>
                      <span className="shrink-0 text-xs px-1.5 py-0.5 bg-violet-600/30 text-violet-300 rounded border border-violet-500/30">{cam.protocol}</span>
                    </div>
                    <p className="text-xs text-slate-400 font-mono mt-0.5 truncate">{cam.host}:{cam.port}</p>
                    {cam.stream_url && <p className="text-xs text-green-400/70 font-mono mt-0.5 truncate">{cam.stream_url}</p>}
                  </div>
                  <button onClick={() => handleDeletePtz(cam.id)}
                    className="shrink-0 px-2 py-1 bg-red-600/30 hover:bg-red-600/60 text-red-300 text-xs rounded border border-red-500/40">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
      }
    </div>
  );

  // ── Rendu principal ───────────────────────────────────────────────────────

  return (
    <>
      <div className="fixed inset-0 bg-black/[0.72] backdrop-blur-[8px] flex items-center justify-center p-4 z-50">
        <div className={`bg-[var(--color-Panel-niv1-BG)] rounded-2xl p-6 w-full border border-white/[0.08] shadow-[0_32px_80px_rgba(0,0,0,0.85)] ${
          mode === 'manager' ? 'max-w-3xl max-h-[90vh] overflow-y-auto pt-0 pb-0' : 'max-w-md'}`}>

          {/* En-tête — collé en haut de la fenêtre qui défile (mode gestion). */}
          <div className={`flex justify-between items-center gap-3 mb-5 ${
            mode === 'manager' ? 'sticky top-0 z-10 -mx-6 px-6 pt-5 pb-4 bg-[var(--color-Panel-niv1-BG)] border-b border-white/[0.08]' : ''}`}>
            <div className="flex items-center gap-3 min-w-0">
              <span className="w-10 h-10 rounded-xl bg-[rgba(99,102,241,0.16)] text-[#818cf8] flex items-center justify-center shrink-0">
                <Satellite className="w-5 h-5" />
              </span>
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-white leading-tight">
                  {mode === 'manager' ? 'Gestion des sources distantes' : 'Ajouter une source externe'}
                </h2>
                {mode === 'manager' && (
                  <p className="text-sm text-slate-400 mt-0.5">Smartphones, flux Xtream et HLS, caméras RTMP et PTZ</p>
                )}
              </div>
            </div>
            <button onClick={handleClose} title="Fermer"
              className="w-9 h-9 shrink-0 rounded-lg flex items-center justify-center text-slate-300 hover:text-white bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.08]">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Masqué dans la fenêtre d'ajout : l'opérateur n'a plus à relever l'UUID
              lui-même, le lien de connexion ou le mail d'invitation le portent. */}
          {mode === 'manager' && (
            <div className="mb-6 p-4 bg-[rgba(99,102,241,0.08)] border border-[rgba(129,140,248,0.3)] rounded-xl space-y-3">
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-blue-300 font-semibold uppercase tracking-wide mb-1">UUID de la chaîne</p>
                  <p className="font-mono text-sm text-blue-100 break-all select-all">{streamPath}</p>
                </div>
                <CopyChip text={streamPath} label="Copier l'UUID" />
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-blue-300 font-semibold uppercase tracking-wide mb-1">Page de connexion des smartphones</p>
                  <p className="text-xs text-blue-200/80 font-mono break-all select-all">{`${webRtcPageUrl}/${streamPath}`}</p>
                </div>
                <CopyChip text={`${webRtcPageUrl}/${streamPath}`} label="Copier l'adresse" />
              </div>
            </div>
          )}

          {mode !== 'manager' && renderCreationBlock()}

          {mode === 'manager' && (
            <>
              <div className="space-y-2 mb-6">
                <SectionTitle icon={<Smartphone className="w-4 h-4" />} label="Smartphones et navigateurs" count={remoteDevices.length} accent="bg-violet-500/15 text-violet-300">
                  <button onClick={() => setShowPasswords(v => !v)}
                    className="text-sm text-blue-300 hover:text-blue-200 flex items-center gap-1.5 px-2.5 py-1 rounded-lg hover:bg-blue-500/10">
                    {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    {showPasswords ? 'Masquer' : 'Afficher'} les liens directs
                  </button>
                  <button onClick={onLoadDevices} title="Actualiser la liste"
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-300 hover:text-white bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.08]">
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </SectionTitle>
                {remoteDevices.map(device => {
                  const isEditing = editState?.device_id === device.device_id;
                  const authMode  = resolveAuthMode(device);
                  const isUuid    = authMode === 'uuid';
                  const link      = resolveAccessLink(device);
                  const transport = resolveTransport(device);
                  return (
                    <div key={device.device_id}
                      className={`rounded-xl border transition-colors ${isEditing ? 'bg-[rgba(99,102,241,0.14)] border-[rgba(129,140,248,0.5)]' : 'bg-white/[0.03] border-white/[0.08] hover:border-white/[0.16]'}`}>
                      {!isEditing && (
                        <div className="p-3">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 min-w-0">
                                <p className="text-white font-mono font-medium truncate">
                                  {isUuid ? device.label : device.device_id}
                                </p>
                                {/* Invité monté depuis Kleon.tv (« Monter sur le plateau ») : admin-api
                                    crée sa caméra avec ce suffixe. Le retirer ici supprime la caméra
                                    sans clore sa demande : préférer « Retirer » du dock Public. */}
                                {isUuid && device.label.endsWith(' (public)') && (
                                  <span className="shrink-0 text-[11px] px-1.5 py-0.5 rounded bg-violet-600 text-white font-extrabold tracking-wide"
                                        title="Spectateur de Kleon.tv monté sur le plateau">
                                    PUBLIC
                                  </span>
                                )}
                                {/* Mode d'accès : lien direct (uuid) ou saisie identifiant/mot de passe. */}
                                <span
                                  className={`shrink-0 text-[11px] px-1.5 py-0.5 rounded border uppercase tracking-wide ${
                                    isUuid
                                      ? 'bg-blue-600/30 text-blue-300 border-blue-500/30'
                                      : 'bg-white/[0.06] text-slate-300 border-slate-500/40'}`}
                                  title={isUuid
                                    ? 'Accès par lien direct : aucune saisie sur le téléphone.'
                                    : 'Accès par identifiant et mot de passe saisis sur le téléphone.'}
                                >
                                  {isUuid ? 'Lien' : 'Login'}
                                </span>
                                {/* Transport média — en vigueur si connecté, prévu sinon (opacité réduite). */}
                                <span
                                  className={`shrink-0 text-[11px] px-1.5 py-0.5 rounded border uppercase tracking-wide ${
                                    transport.mode === 'sfu'
                                      ? 'bg-emerald-600/30 text-emerald-300 border-emerald-500/30'
                                      : 'bg-amber-600/30 text-amber-300 border-amber-500/30'
                                  } ${transport.effective ? '' : 'opacity-60'}`}
                                  title={transport.effective
                                    ? transport.reason
                                    : `Appareil déconnecté — transport prévu à la prochaine connexion. ${transport.reason}`}
                                >
                                  {transport.mode === 'sfu' ? 'SFU' : 'P2P'}
                                </span>
                              </div>
                            </div>
                            <div className="ml-2 shrink-0 flex items-center gap-2">
                              <button
                                onClick={() => toggleDirector(device.device_id)}
                                title={directorDeviceIds.has(device.device_id)
                                  ? 'Retirer le rôle Réalisateur'
                                  : 'Marquer comme Réalisateur'}
                                className={`px-2 py-1 text-xs rounded border transition-colors ${
                                  directorDeviceIds.has(device.device_id)
                                    ? 'bg-[#25c400]/20 text-[#25c400] border-[#25c400]/50'
                                    : 'bg-white/[0.06] text-slate-400 border-slate-500/40 hover:text-slate-200'}`}>
                                🎬 Réalisateur
                              </button>
                              <span className={`px-2 py-1 text-xs rounded inline-flex items-center gap-1.5 ${device.is_connected ? 'bg-green-500/20 text-green-400' : 'bg-slate-600 text-slate-400'}`}>
                                {device.is_connected ? '● Connecté' : '○ Hors ligne'}
                                {device.is_connected && renderDeviceNetwork(device.device_id)}
                              </span>
                            </div>
                          </div>
                          {/* Hors de la ligne d'en-tête : le lien prend toute la largeur de
                              la carte et peut donc passer sous les badges. Un seul affichage,
                              même en mode identifiant/mot de passe où il les porte. */}
                          {showPasswords && (
                            <p className="text-xs text-blue-300 font-mono mb-2 break-all">
                              🔗 {link || <span className="text-slate-400 italic">lien non disponible</span>}
                            </p>
                          )}
                          <div className="flex gap-2 flex-wrap">
                            <button onClick={() => setEditState({
                                device_id: device.device_id,
                                newId    : device.device_id,
                                password : '',
                                label    : device.label,
                                authMode,
                              })}
                              className="flex items-center gap-1 px-3 py-1 bg-indigo-600/30 hover:bg-indigo-600/60 text-indigo-300 text-xs rounded border border-indigo-500/40">
                              <Pencil className="w-3 h-3" /> Modifier
                            </button>
                            <button onClick={() => openMailModal(device)}
                              className="flex items-center gap-1 px-3 py-1 bg-emerald-600/30 hover:bg-emerald-600/60 text-emerald-300 text-xs rounded border border-emerald-500/40">
                              <Mail className="w-3 h-3" /> Envoyer les infos
                            </button>
                            <button onClick={() => copyAccessLink(device)} disabled={!link}
                              className="flex items-center gap-1 px-3 py-1 bg-blue-600/30 hover:bg-blue-600/60 disabled:opacity-40 disabled:cursor-not-allowed text-blue-300 text-xs rounded border border-blue-500/40">
                              {copiedDevice === device.device_id
                                ? <><Check className="w-3 h-3" /> Copié</>
                                : <>🔗 Copier le lien</>}
                            </button>
                            {device.is_connected && (
                              <button onClick={() => onDisconnectDevice(device.device_id)}
                                className="flex items-center gap-1 px-3 py-1 bg-orange-600/30 hover:bg-orange-600/60 text-orange-300 text-xs rounded border border-orange-500/40">
                                <Unplug className="w-3 h-3" /> Déconnecter
                              </button>
                            )}
                            <button onClick={() => onDeleteDevice(device.device_id)}
                              className="ml-auto px-3 py-1 bg-red-600/30 hover:bg-red-600/60 text-red-300 text-xs rounded border border-red-500/40">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      )}
                      {isEditing && editState && (
                        <div className="p-3 space-y-3">
                          <p className="text-xs text-indigo-300 font-semibold uppercase tracking-wide">Modification</p>
                          {editState.authMode === 'uuid' ? (
                            <>
                              <input type="text" value={editState.label} placeholder="Nom d'affichage"
                                onChange={e => setEditState(s => s ? { ...s, label: e.target.value } : s)}
                                className="w-full bg-slate-600 text-white rounded px-3 py-2 text-sm border border-slate-500 focus:border-indigo-400 outline-none" />
                              <p className="text-xs text-slate-400">
                                Ce device se connecte par lien : il n'a ni identifiant à saisir ni mot de passe.
                              </p>
                            </>
                          ) : (
                            <>
                              <input type="text" value={editState.newId}
                                onChange={e => setEditState(s => s ? { ...s, newId: e.target.value } : s)}
                                className="w-full bg-slate-600 text-white rounded px-3 py-2 font-mono text-sm border border-slate-500 focus:border-indigo-400 outline-none" />
                              <input type="text" value={editState.password} placeholder="Nouveau mot de passe (vide = inchangé)"
                                onChange={e => setEditState(s => s ? { ...s, password: e.target.value } : s)}
                                className="w-full bg-slate-600 text-white rounded px-3 py-2 font-mono text-sm border border-slate-500 focus:border-indigo-400 outline-none" />
                            </>
                          )}
                          <div className="flex gap-2">
                            <button onClick={handleSaveEdit}
                              disabled={editSaving || (editState.authMode === 'uuid' ? !editState.label.trim() : !editState.newId.trim())}
                              className="flex-1 flex items-center justify-center gap-2 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white text-sm rounded font-semibold">
                              {editSaving ? '⏳ Enregistrement…' : <><Check className="w-4 h-4" /> Enregistrer</>}
                            </button>
                            <button onClick={() => setEditState(null)} disabled={editSaving}
                              className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-slate-300 text-sm rounded flex items-center gap-1 disabled:opacity-50">
                              <X className="w-4 h-4" /> Annuler
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {remoteDevices.length === 0 && (
                  <p className="text-slate-400 text-sm italic text-center py-4">Aucune source Smartphone/Web enregistrée</p>
                )}
              </div>

              <div className="border-t border-slate-700 mb-6" />
              {renderXtreamList()}
              <div className="border-t border-slate-700 mb-6" />
              {renderHlsList()}
              <div className="border-t border-slate-700 mb-6" />
              {renderRtmpList()}
              <div className="border-t border-slate-700 mb-6" />
              {renderPtzList()}

              {/* Pied — collé en bas de la fenêtre qui défile. */}
              <div className="sticky bottom-0 -mx-6 px-6 py-4 mt-2 bg-[var(--color-Panel-niv1-BG)] border-t border-white/[0.08]">
                <button onClick={handleClose}
                  className="w-full py-3 bg-[#6366f1] hover:bg-[#5558e6] border border-[#818cf8] text-white rounded-lg font-semibold shadow-[0_4px_14px_rgba(99,102,241,0.35)]">
                  Fermer
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Sélecteur de chaîne Xtream (création) */}
      {showXtreamSelector && xtreamStreams && (
        <XtreamStreamSelector
          streams={xtreamStreams}
          onSelect={handleXtreamStreamPicked}
          onClose={() => setShowXtreamSelector(false)}
        />
      )}

      {/* ── Modale d'édition HLS libre ─────────────────────────────────── */}
      {editHlsState && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-lg border border-slate-700 shadow-2xl">
            <div className="flex justify-between items-start mb-5">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Pencil className="w-5 h-5 text-indigo-400" /> Modifier le flux HLS
              </h3>
              <button onClick={() => setEditHlsState(null)} className="text-slate-400 hover:text-white text-2xl leading-none">×</button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Nom affiché</label>
                <input type="text"
                  value={editHlsState.label}
                  onChange={e => setEditHlsState(s => s ? { ...s, label: e.target.value } : s)}
                  className="w-full bg-slate-600 text-white rounded px-3 py-2 text-sm border border-slate-500 focus:border-cyan-400 outline-none" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">URL (.m3u8)</label>
                <input type="text"
                  value={editHlsState.url}
                  onChange={e => setEditHlsState(s => s ? { ...s, url: e.target.value, error: null } : s)}
                  className={`w-full bg-slate-600 text-white rounded px-3 py-2 text-sm border outline-none font-mono ${
                    editHlsState.error ? 'border-orange-500' : 'border-slate-500 focus:border-cyan-400'}`} />
                {editHlsState.error && <p className="text-orange-400 text-xs mt-1">❌ {editHlsState.error}</p>}
              </div>
            </div>

            <div className="flex gap-3 mt-5">
              <button
                onClick={handleSaveEditHls}
                disabled={editHlsState.saving}
                className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded font-semibold text-sm flex items-center justify-center gap-2"
              >
                {editHlsState.saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Enregistrer
              </button>
              <button onClick={() => setEditHlsState(null)} className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-slate-300 rounded text-sm">
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modale de renommage d'une caméra RTMP ──────────────────────── */}
      {/* Seul le nom bouge : la clé est l'identité de la caméra, la changer
          reviendrait à en créer une autre et à couper le cadreur en plein direct. */}
      {editRtmpState && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-lg border border-slate-700 shadow-2xl">
            <div className="flex justify-between items-start mb-5">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Pencil className="w-5 h-5 text-emerald-400" /> Renommer la caméra RTMP
              </h3>
              <button onClick={() => setEditRtmpState(null)} className="text-slate-400 hover:text-white text-2xl leading-none">×</button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Nom affiché</label>
                <input type="text"
                  autoFocus
                  value={editRtmpState.label}
                  onChange={e => setEditRtmpState(s => s ? { ...s, label: e.target.value, error: null } : s)}
                  onKeyDown={e => { if (e.key === 'Enter' && !editRtmpState.saving) handleSaveEditRtmp(); }}
                  className="w-full bg-slate-600 text-white rounded px-3 py-2 text-sm border border-slate-500 focus:border-cyan-400 outline-none" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">URL de publication (inchangeable)</label>
                <input readOnly value={rtmpIngestUrlOf(editRtmpState.stream)}
                  className="w-full bg-slate-900 text-slate-400 rounded px-3 py-2 text-xs border border-slate-600 font-mono select-all" />
              </div>
              {editRtmpState.error && <p className="text-orange-400 text-xs">❌ {editRtmpState.error}</p>}
            </div>

            <div className="flex gap-3 mt-5">
              <button
                onClick={handleSaveEditRtmp}
                disabled={editRtmpState.saving}
                className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded font-semibold text-sm flex items-center justify-center gap-2"
              >
                {editRtmpState.saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Enregistrer
              </button>
              <button onClick={() => setEditRtmpState(null)} className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-slate-300 rounded text-sm">
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modale d'édition Xtream, étape 1 : l'alias ─────────────────── */}
      {editXtreamAliasState && !editXtreamState && !showEditXtreamSelector && (() => {
        const stream      = editXtreamAliasState.stream;
        const account     = xtreamAccounts.find(a => a.id === stream.xtream_account_id);
        const channelName = xtreamChannelLabel(stream);
        const summary     = xtreamSourceSummary(stream, account);
        return (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
            <div className="bg-slate-800 rounded-xl p-6 w-full max-w-lg border border-slate-700 shadow-2xl">
              <div className="flex justify-between items-start mb-5">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Pencil className="w-5 h-5 text-pink-400" /> Modifier la source Xtream
                </h3>
                <button onClick={() => setEditXtreamAliasState(null)} className="text-slate-400 hover:text-white text-2xl leading-none">×</button>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Nom affiché</label>
                  <input type="text"
                    value={editXtreamAliasState.alias}
                    placeholder={channelName ?? stream.label}
                    onChange={e => setEditXtreamAliasState(st => st ? { ...st, alias: e.target.value, error: null } : st)}
                    className="w-full bg-slate-600 text-white rounded px-3 py-2 text-sm border border-slate-500 focus:border-pink-400 outline-none" />
                </div>

                {/* Le flux lui-même ne se modifie pas ici : on le rappelle, et le
                    crayon mène à la fenêtre qui sait le changer. */}
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Flux</label>
                  <div className="flex items-center justify-between gap-2 bg-slate-700/50 border border-slate-600 rounded-lg p-3">
                    <div className="min-w-0">
                      <p className="text-sm text-white font-mono truncate" title={summary ?? undefined}>
                        {summary ?? '— flux inconnu —'}
                      </p>
                    </div>
                    <button
                      onClick={() => openEditXtreamSource(stream, editXtreamAliasState.alias)}
                      title="Modifier le flux"
                      className="shrink-0 px-2 py-1 bg-indigo-600/30 hover:bg-indigo-600/60 text-indigo-300 text-xs rounded border border-indigo-500/40"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                {editXtreamAliasState.error && (
                  <p className="text-orange-400 text-xs">❌ {editXtreamAliasState.error}</p>
                )}
              </div>

              <div className="flex gap-3 mt-5">
                <button
                  onClick={handleSaveEditXtreamAlias}
                  disabled={editXtreamAliasState.saving}
                  className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded font-semibold text-sm flex items-center justify-center gap-2"
                >
                  {editXtreamAliasState.saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Enregistrer
                </button>
                <button onClick={() => setEditXtreamAliasState(null)} className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-slate-300 rounded text-sm">
                  Annuler
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Modale d'édition Xtream ────────────────────────────────────── */}
      {editXtreamState && !showEditXtreamSelector && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-lg border border-slate-700 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Pencil className="w-5 h-5 text-pink-400" /> Changer le flux Xtream
              </h3>
              <button onClick={() => setEditXtreamState(null)} className="text-slate-400 hover:text-white text-2xl leading-none">×</button>
            </div>

            <p className="text-xs text-slate-400 mb-4">
              Source actuelle : <span className="text-white font-medium">{editXtreamState.stream.label}</span>
            </p>

            <div className="space-y-3">
              {xtreamAccounts.length > 0 && (
                <div className="flex gap-1 bg-slate-900/40 p-1 rounded-lg border border-slate-700">
                  <button
                    onClick={() => setEditXtreamState(s => s ? { ...s, mode: 'existing', error: null } : s)}
                    className={`flex-1 py-1.5 px-3 rounded text-xs font-semibold transition-colors ${
                      editXtreamState.mode === 'existing' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-slate-200'}`}>
                    Compte enregistré
                  </button>
                  <button
                    onClick={() => setEditXtreamState(s => s ? { ...s, mode: 'new', error: null } : s)}
                    className={`flex-1 py-1.5 px-3 rounded text-xs font-semibold transition-colors ${
                      editXtreamState.mode === 'new' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-slate-200'}`}>
                    Nouveau serveur
                  </button>
                </div>
              )}

              {editXtreamState.mode === 'existing' && xtreamAccounts.length > 0 && (
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Compte</label>
                  <select
                    value={editXtreamState.selectedAccountId}
                    onChange={e => setEditXtreamState(s => s ? { ...s, selectedAccountId: e.target.value, error: null } : s)}
                    className="w-full bg-slate-600 text-white rounded px-3 py-2 text-sm border border-slate-500 focus:border-pink-400 outline-none"
                  >
                    <option value="">— Choisir —</option>
                    {xtreamAccounts.map(acc => (
                      <option key={acc.id} value={acc.id}>
                        {acc.username} @ {acc.server.replace(/^https?:\/\//, '')}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {(editXtreamState.mode === 'new' || xtreamAccounts.length === 0) && (
                <>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Serveur</label>
                    <input type="text"
                      value={editXtreamState.xtreamServer}
                      placeholder="http://mon-serveur.com"
                      onChange={e => setEditXtreamState(s => s ? { ...s, xtreamServer: e.target.value, error: null } : s)}
                      className="w-full bg-slate-600 text-white rounded px-3 py-2 text-sm border border-slate-500 focus:border-pink-400 outline-none font-mono" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Identifiant</label>
                      <input type="text"
                        value={editXtreamState.xtreamUser}
                        placeholder="Nom d'utilisateur"
                        onChange={e => setEditXtreamState(s => s ? { ...s, xtreamUser: e.target.value, error: null } : s)}
                        className="w-full bg-slate-600 text-white rounded px-3 py-2 text-sm border border-slate-500 focus:border-pink-400 outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Mot de passe</label>
                      <input type="password"
                        value={editXtreamState.xtreamPass}
                        placeholder="Mot de passe"
                        onChange={e => setEditXtreamState(s => s ? { ...s, xtreamPass: e.target.value, error: null } : s)}
                        className="w-full bg-slate-600 text-white rounded px-3 py-2 text-sm border border-slate-500 focus:border-pink-400 outline-none" />
                    </div>
                  </div>
                </>
              )}

              {editXtreamState.error && <p className="text-red-400 text-xs">❌ {editXtreamState.error}</p>}

              <button
                onClick={handleEditXtreamFetch}
                disabled={editXtreamState.loading}
                className="w-full py-2 bg-pink-600/20 hover:bg-pink-600/40 disabled:opacity-50 disabled:cursor-not-allowed text-pink-300 border border-pink-500/40 rounded text-sm font-semibold flex items-center justify-center gap-2"
              >
                {editXtreamState.loading
                  ? <><RefreshCw className="w-4 h-4 animate-spin" /> Chargement…</>
                  : 'Récupérer les flux'}
              </button>

              {editXtreamState.selectedStream && (
                <>
                  <div className="p-3 bg-green-900/20 border border-green-600/40 rounded-lg">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <p className="text-xs text-green-400 font-semibold uppercase tracking-wide shrink-0">Nouvelle chaîne</p>
                        <p className="text-sm text-white font-mono truncate">{editXtreamState.selectedStream.name}</p>
                      </div>
                      <button
                        onClick={() => { if (editXtreamState.streams) setShowEditXtreamSelector(true); }}
                        className="shrink-0 text-xs text-slate-400 hover:text-slate-200 underline"
                      >
                        Changer
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex gap-3 mt-5 pt-4 border-t border-slate-700">
              <button
                onClick={handleSaveEditXtream}
                disabled={!editXtreamState.selectedStream}
                className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded font-semibold text-sm flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" /> Enregistrer
              </button>
              <button onClick={() => setEditXtreamState(null)} className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-slate-300 rounded text-sm flex items-center gap-1">
                <X className="w-4 h-4" /> Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sélecteur de chaîne Xtream (édition) */}
      {showEditXtreamSelector && editXtreamState?.streams && (
        <XtreamStreamSelector
          streams={editXtreamState.streams}
          onSelect={(stream) => { handleEditXtreamStreamPicked(stream); }}
          onClose={() => setShowEditXtreamSelector(false)}
        />
      )}

      {/* ── Mail modal ────────────────────────────────────────────────────── */}
      {mailState && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-lg border border-slate-700 shadow-2xl">
            <div className="flex justify-between items-start mb-5">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Mail className="w-5 h-5 text-emerald-400" /> Envoyer les informations de connexion
              </h3>
              <button onClick={() => setMailState(null)} className="text-slate-400 hover:text-white text-2xl leading-none">×</button>
            </div>
            <div className="mb-5 p-3 bg-slate-700/60 rounded-lg border border-slate-600 space-y-2 text-sm">
              <div className="flex gap-2"><span className="text-slate-400 w-32 shrink-0">URL WebRTC :</span><span className="text-blue-300 font-mono text-xs break-all">{buildInvitationUrl(mailState.device, mailState.password)}</span></div>
              {resolveAuthMode(mailState.device) === 'uuid' ? (
                <>
                  <div className="flex gap-2"><span className="text-slate-400 w-32 shrink-0">Nom d'affichage :</span><span className="text-white font-mono">{mailState.device.label}</span></div>
                  <p className="text-xs text-slate-400">Connexion par lien : le porteur n'a rien à saisir.</p>
                </>
              ) : (
                <>
                  <div className="flex gap-2"><span className="text-slate-400 w-32 shrink-0">Identifiant :</span><span className="text-white font-mono">{mailState.device.device_id}</span></div>
                  <div className="flex gap-2"><span className="text-slate-400 w-32 shrink-0">Mot de passe :</span>
                    <span className="text-yellow-300 font-mono">{mailState.password || <span className="text-slate-400 italic text-xs">non disponible</span>}</span>
                  </div>
                </>
              )}
            </div>
            {!mailState.success && (
              <>
                {/* Même saisie que le bloc d'ajout : une zone, les deux canaux
                    mélangés, et le carnet pour la remplir. */}
                <label className="block text-xs text-slate-400 mb-1">
                  Destinataires — email ou WhatsApp, Entrée ou virgule pour valider
                </label>
                <div className="flex gap-2 items-start mb-4">
                  <div className="flex-1 min-w-0">
                    <RecipientChipInput
                      value={mailState.recipients}
                      onChange={recipients => setMailState(s => s ? { ...s, recipients, error: null } : s)}
                    />
                  </div>
                  <Trigger
                    iconName   = "bookUser"
                    iconWidth  = {30}
                    iconHeight = {30}
                    onClick    = {() => setAddressBookTarget('mail')}
                  />
                </div>
                {mailState.error && <p className="text-red-400 text-sm mb-3">❌ {mailState.error}</p>}
                <div className="flex gap-3">
                  <button onClick={handleSendMail}
                    disabled={mailState.sending || mailState.recipients.filter(isValidRecipient).length === 0}
                    className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg font-semibold flex items-center justify-center gap-2">
                    {mailState.sending ? '⏳ Envoi…' : <><Mail className="w-4 h-4" /> Envoyer</>}
                  </button>
                  <button onClick={() => setMailState(null)} className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-slate-300 rounded-lg text-sm">Annuler</button>
                </div>
              </>
            )}
            {mailState.success && (
              <div className="text-center py-4">
                <p className="text-2xl mb-2">✅</p>
                <p className="text-green-400 font-semibold mb-1">Informations envoyées !</p>
                <p className="text-slate-400 text-sm mb-4">Informations envoyées à <strong className="text-white">{mailState.sentTo.join(', ')}</strong>.</p>
                {mailState.error && <p className="text-red-400 text-sm mb-4">❌ {mailState.error}</p>}
                <button onClick={() => setMailState(null)} className="px-6 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg">Fermer</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Modale de renvoi des infos RTMP — même schéma que le Mail modal
          ci-dessus, pour une caméra déjà enregistrée dans la liste. ────────── */}
      {rtmpMailState && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-lg border border-slate-700 shadow-2xl">
            <div className="flex justify-between items-start mb-5">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Mail className="w-5 h-5 text-emerald-400" /> Envoyer les informations de publication
              </h3>
              <button onClick={() => setRtmpMailState(null)} className="text-slate-400 hover:text-white text-2xl leading-none">×</button>
            </div>
            <div className="mb-5 p-3 bg-slate-700/60 rounded-lg border border-slate-600 space-y-2 text-sm">
              <div className="flex gap-2"><span className="text-slate-400 w-32 shrink-0">Caméra :</span><span className="text-white font-mono">{rtmpMailState.camera.label}</span></div>
              <div className="flex gap-2"><span className="text-slate-400 w-32 shrink-0">Serveur :</span><span className="text-blue-300 font-mono text-xs break-all">{rtmpServerUrl()}</span></div>
              <div className="flex gap-2"><span className="text-slate-400 w-32 shrink-0">Clé :</span><span className="text-yellow-300 font-mono text-xs break-all">{rtmpStreamKeyOf(rtmpMailState.camera)}</span></div>
            </div>
            {!rtmpMailState.success && (
              <>
                <label className="block text-xs text-slate-400 mb-1">
                  Destinataires — email ou WhatsApp, Entrée ou virgule pour valider
                </label>
                <div className="flex gap-2 items-start mb-4">
                  <div className="flex-1 min-w-0">
                    <RecipientChipInput
                      value={rtmpMailState.recipients}
                      onChange={recipients => setRtmpMailState(s => s ? { ...s, recipients, error: null } : s)}
                    />
                  </div>
                  <Trigger
                    iconName   = "bookUser"
                    iconWidth  = {30}
                    iconHeight = {30}
                    onClick    = {() => setAddressBookTarget('rtmp-resend')}
                  />
                </div>
                {rtmpMailState.error && <p className="text-red-400 text-sm mb-3">❌ {rtmpMailState.error}</p>}
                <div className="flex gap-3">
                  <button onClick={handleSendRtmpMail}
                    disabled={rtmpMailState.sending || rtmpMailState.recipients.filter(isValidRecipient).length === 0}
                    className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg font-semibold flex items-center justify-center gap-2">
                    {rtmpMailState.sending ? '⏳ Envoi…' : <><Mail className="w-4 h-4" /> Envoyer</>}
                  </button>
                  <button onClick={() => setRtmpMailState(null)} className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-slate-300 rounded-lg text-sm">Annuler</button>
                </div>
              </>
            )}
            {rtmpMailState.success && (
              <div className="text-center py-4">
                <p className="text-2xl mb-2">✅</p>
                <p className="text-green-400 font-semibold mb-1">Informations envoyées !</p>
                <p className="text-slate-400 text-sm mb-4">Informations envoyées à <strong className="text-white">{rtmpMailState.sentTo.join(', ')}</strong>.</p>
                {rtmpMailState.error && <p className="text-red-400 text-sm mb-4">❌ {rtmpMailState.error}</p>}
                <button onClick={() => setRtmpMailState(null)} className="px-6 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg">Fermer</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Carnet d'adresses — porté sur document.body pour sortir du contexte
          d'empilement du panneau, comme dans le dashboard de la chaîne. */}
      {addressBookTarget && ReactDOM.createPortal(
        <AddressBookModal
          userId             = {authentication.admin ? authentication.admin.id : 0}
          onClose            = {() => setAddressBookTarget(null)}
          onSelectRecipients = {handleAddressBookSelect}
        />,
        document.body
      )}
    </>
  );
};

export default ProductionControlRoom_RemoteDevicesPanel;